import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { BeakerIcon, BookmarkIcon, BookOpenIcon, LinkIcon, ScissorsIcon, SparklesIcon, SpeakerWaveIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AUDIO_LEVELS } from '../lib/audioLevels';
import { duckSoundEffects, playSoundEffect } from '../lib/sfx';
import { DEFAULT_TIER, TIER_INFO, TIER_ORDER_EASY_TO_HARD, getAvailableTiers } from '../lib/learningTiers';
import { useI18n } from '../i18n/I18nProvider';
import ReorderingQuestion from '../components/ReorderingQuestion';
import DictationQuestion from '../components/DictationQuestion';
import PronunciationBoard from '../components/PronunciationBoard';
import TrainingModeSelector, { TrainingModeBadge } from '../components/TrainingModeSelector';
import CustomReviewSelector from '../components/CustomReviewSelector';
import {
  assignmentItemKey,
  assignmentTaskKey,
  createPracticeProgress,
  findPracticeItem,
  normalizeAssignmentItem,
  normalizePracticeProgress,
} from '../lib/practiceAssignments';
import {
  reviewResultAfterFourSteps,
  reviewResultsFromPretest,
} from '../lib/reviewCompletion';
import { mergeReorderTokens } from '../lib/reorderChunks';
import { splitHighlightedText } from '../utils/highlightedText';
import {
  createStudySessionId,
  generateDistractors,
  getCleanWord,
  isPureEnglish,
  practiceAudioRequests,
  shuffleArray,
  shuffleDifferentOrder,
  zhDominates,
} from '../lib/wordPracticeUtils';
import {
  beginAudioSession,
  disposeAudioSession,
  getSessionAudioUrl,
  prefetchSessionAudio,
} from '../lib/sessionAudioCache';
import {
  TRAINING_MODE_COMPREHENSIVE,
  TRAINING_MODE_MEANING,
  buildPendingTrainingLaunch,
  countTrainingModes,
  filterByTrainingMode,
  getAvailableTrainingModeCounts,
  getTrainingMode,
  normalizeTrainingMode,
  resolveInitialTrainingMode,
} from '../lib/trainingModes';
import {
  buildComprehensiveDictationChoices,
  buildComprehensiveMeaningChoices,
  buildComprehensiveQuestion,
  chunkComprehensiveItems,
  claimComprehensiveCompletion,
  completedComprehensiveCount,
  completedComprehensiveSteps,
  comprehensiveItemKey,
  createComprehensiveProgress,
  getDefinitionCn,
  isSpellingCorrect,
  nextComprehensiveActivity,
  normalizeComprehensiveItem,
  updateComprehensiveProgress,
} from '../lib/comprehensiveTraining';
import {
  buildCustomReviewPracticePayload,
  clearCustomReviewSelection,
  matchingCustomReviewResume,
  readCustomReviewSelection,
  writeCustomReviewSelection,
} from '../lib/customReview';


// --- Utility Functions ---
const encouragingMessages = [
  "It's okay—every mistake is one step closer to success.",
  "That was tricky! You'll get it next time.",
  "Keep going—persistence pays off.",
  "Learning is a journey; this is just one step.",
  "Great effort! The correct answer is your next target."
];

const getRandomEncouragement = () => encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)];

const PRACTICE_STAGE_SUCCESS_SFX = {
  1: { name: 'practiceStage1', volume: 0.72 },
  2: { name: 'practiceStage2', volume: 0.8 },
  3: { name: 'practiceStage3', volume: 0.88 },
  4: { name: 'practiceStage4', volume: 0.84 },
};

const getPracticeStageSuccessSfx = (stage) => PRACTICE_STAGE_SUCCESS_SFX[stage] || { name: 'correct', volume: 0.88 };

const playPronunciationTap = (word) => {
  playSoundEffect('replayTick', { volume: 0.54, throttleMs: 80, throttleKey: `tts-tap-${word}` });
  playTTS(getCleanWord(word), 'word');
};

// --- Shared TTS Utilities ---
let __sharedAudio = null;
let __ttsEpoch = 0;
let __releaseSfxDuck = null;

const ensureSharedAudio = () => {
  if (!__sharedAudio) {
    try { __sharedAudio = new Audio(); } catch (_) { __sharedAudio = null; }
  }
  if (__sharedAudio) {
    __sharedAudio.volume = AUDIO_LEVELS.tts;
    __sharedAudio.preload = 'auto';
  }
  return __sharedAudio;
};

const releaseSfxDuck = () => {
  if (typeof __releaseSfxDuck === 'function') {
    try { __releaseSfxDuck(); } catch (_) {}
  }
  __releaseSfxDuck = null;
};

const engageSfxDuck = () => {
  releaseSfxDuck();
  try {
    __releaseSfxDuck = duckSoundEffects();
  } catch (_) {
    __releaseSfxDuck = null;
  }
};

export const skipAllTTS = () => {
  __ttsEpoch += 1;
  try {
    releaseSfxDuck();
    const audio = ensureSharedAudio();
    if (audio) {
      try { audio.pause(); } catch (_) {}
      audio.src = '';
    }
  } catch (_) {}
};

export const cancelAllTTS = skipAllTTS;

export const playTTS = async (text, mode = 'sentence') => {
  if (!text) return;
  const epoch = __ttsEpoch;
  try {
    // Stop any current playback
    const audio = ensureSharedAudio();
    if (audio) {
      try { audio.pause(); } catch (_) {}
      audio.src = '';
    }
    releaseSfxDuck();
    const url = await getSessionAudioUrl(text, mode);
    if (epoch !== __ttsEpoch) return; // stale
    if (!audio) return;
    audio.volume = AUDIO_LEVELS.tts;
    audio.src = url;
    engageSfxDuck();
    const cleanup = () => {
      audio.onended = null;
      audio.onerror = null;
      releaseSfxDuck();
    };
    audio.onended = cleanup;
    audio.onerror = cleanup;
    await audio.play().catch(() => { cleanup(); });
  } catch (_) {
    // swallow
  }
};

const playDictionaryPronunciation = async (audioUrl, word) => {
  playSoundEffect('replayTick', {
    volume: 0.54,
    throttleMs: 80,
    throttleKey: `dictionary-audio-${word}`,
  });
  if (!audioUrl) {
    await playTTS(word, 'word');
    return;
  }
  const audio = ensureSharedAudio();
  if (!audio) {
    await playTTS(word, 'word');
    return;
  }
  try {
    skipAllTTS();
    audio.volume = AUDIO_LEVELS.tts;
    audio.src = audioUrl;
    engageSfxDuck();
    const cleanup = () => {
      audio.onended = null;
      audio.onerror = null;
      releaseSfxDuck();
    };
    audio.onended = cleanup;
    audio.onerror = () => {
      cleanup();
      playTTS(word, 'word');
    };
    await audio.play();
  } catch (_) {
    releaseSfxDuck();
    await playTTS(word, 'word');
  }
};

export const playTTSOnceWait = async (text, maxWaitMs = 2200, mode = 'sentence') => {
  if (!text) return;
  const startEpoch = __ttsEpoch;
  try {
    const audio = ensureSharedAudio();
    if (audio) { try { audio.pause(); } catch (_) {} audio.src = ''; }
    releaseSfxDuck();
    const url = await getSessionAudioUrl(text, mode);
    if (startEpoch !== __ttsEpoch) return; // cancelled
    if (!audio) return;
    audio.volume = AUDIO_LEVELS.tts;
    audio.src = url;
    engageSfxDuck();
    await new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        audio.onended = null;
        audio.onerror = null;
        releaseSfxDuck();
        resolve();
      };
      const to = setTimeout(finish, Math.max(1500, Math.min(20000, maxWaitMs)));
      audio.onended = () => { clearTimeout(to); finish(); };
      audio.onerror = () => { clearTimeout(to); finish(); };
      audio.play().catch(() => { clearTimeout(to); finish(); });
    });
  } catch (_) {
    // ignore
  }
};

// --- Auto Audio Preference Helpers ---
const AUTO_AUDIO_KEY = 'wp_auto_audio';
const getAutoAudioPref = (fallback = true) => {
  try {
    const v = localStorage.getItem(AUTO_AUDIO_KEY);
    if (v === null || v === undefined) return fallback;
    return v === '1' || v === 'true';
  } catch (_) {
    return fallback;
  }
};
const setAutoAudioPref = (val) => {
  try { localStorage.setItem(AUTO_AUDIO_KEY, val ? '1' : '0'); } catch (_) {}
};

// --- API Fetcher ---
const fetchPracticeSessionData = async (items, tier, token, metadata = {}) => {
    const body = metadata?.review_source === 'custom'
      ? buildCustomReviewPracticePayload({ items, tier, ...metadata })
      : { items, tier, ...metadata };
    const res = await fetch('/api/student/practice-session', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to fetch practice data');
    }
    return res.json();
};

// --- Child Components ---

// (no resume modal or cookie helpers)

const ENGLISH_WORD_PATTERN = /^[A-Za-z]+(?:['’][A-Za-z]+)*$/;

const CapturableText = ({
  text,
  captureMode = false,
  onCaptureWord,
  emphasized = false,
  highlightGroup = false,
  keyPrefix = 'word',
}) => {
  const { t } = useI18n();
  const parts = String(text || '').split(/([A-Za-z]+(?:['’][A-Za-z]+)*)/g);
  return parts.map((part, index) => {
    if (!ENGLISH_WORD_PATTERN.test(part)) {
      return <React.Fragment key={`${keyPrefix}-p-${index}`}>{part}</React.Fragment>;
    }
    const emphasisClass = emphasized
      ? 'font-bold text-blue-600 bg-blue-100'
      : highlightGroup
        ? 'font-bold text-blue-600 hover:text-indigo-700 hover:bg-blue-200'
      : 'font-medium text-gray-900 hover:text-indigo-700 hover:bg-indigo-50';
    if (!captureMode) {
      return emphasized ? (
        <strong key={`${keyPrefix}-e-${index}`} className="px-1 py-0.5 rounded-sm font-bold text-blue-600 bg-blue-100">
          {part}
        </strong>
      ) : (
        <React.Fragment key={`${keyPrefix}-w-${index}`}>{part}</React.Fragment>
      );
    }
    return (
      <button
        type="button"
        key={`${keyPrefix}-c-${index}`}
        data-i18n-skip
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onCaptureWord?.(part);
        }}
        className={`${highlightGroup ? 'inline px-0.5' : 'mx-0.5 inline px-1'} rounded py-0.5 underline decoration-dotted underline-offset-4 ${emphasisClass}`}
        title={t('Save "{word}" to a wordbook', { word: part })}
        aria-label={t('Save {word} to a wordbook', { word: part })}
      >
        {part}
      </button>
    );
  });
};

const HighlightedSentence = ({
  sentence,
  isPureMode,
  targetWord,
  captureMode = false,
  onCaptureWord,
  includeLegacyMarkers = false,
}) => {
    const parts = splitHighlightedText(sentence, {
      includeLegacy: includeLegacyMarkers,
    });

    const cleanTarget = (targetWord ? getCleanWord(targetWord) : '').trim();
    const renderText = (text, keyBase, bracketed = false) => {
      if (bracketed) {
        return (
          <CapturableText
            text={text}
            captureMode={captureMode}
            onCaptureWord={onCaptureWord}
            highlightGroup
            keyPrefix={`${keyBase}-group`}
          />
        );
      }
      const tokens = String(text || '').split(/([A-Za-z]+(?:['’][A-Za-z]+)*)/g);
      return tokens.map((token, index) => {
        const isWord = ENGLISH_WORD_PATTERN.test(token);
        if (!isWord) return <React.Fragment key={`${keyBase}-p-${index}`}>{token}</React.Fragment>;
        const emphasized = (
          cleanTarget && token.toLowerCase() === cleanTarget.toLowerCase()
        );
        return (
          <CapturableText
            key={`${keyBase}-w-${index}`}
            text={token}
            captureMode={captureMode}
            onCaptureWord={onCaptureWord}
            emphasized={emphasized}
            keyPrefix={`${keyBase}-${index}`}
          />
        );
      });
    };

    return (
        <p data-i18n-skip className="text-lg mb-4 leading-relaxed">
            {parts.map((part, i) => {
                if (part.highlighted) {
                    // Bracket priority: highlight bracketed content as a whole;
                    // do not additionally highlight the target inside.
                    const word = part.text;
                    const displayWord = isPureMode ? getCleanWord(word) : word;
                    return (
                        <strong
                          key={`b-${i}`}
                          className="inline rounded-md bg-blue-100 px-1 py-0.5 font-bold text-blue-600"
                        >
                          {renderText(displayWord, `b-${i}`, true)}
                        </strong>
                    );
                }
                // Non-bracket text: additionally highlight all occurrences of the target word
                return <React.Fragment key={`t-${i}`}>{renderText(part.text, `nb-${i}`)}</React.Fragment>;
            })}
        </p>
    );
};

export const LegacyScramblePuzzle = ({
  scrambled,
  onSubmit,
  disabled = false,
  captureMode = false,
  onCaptureWord,
}) => {
    const { t } = useI18n();
    const initialWords = useMemo(() => scrambled.map((word, index) => ({ word, id: index })), [scrambled]);
    const [selected, setSelected] = useState([]);
    const [options, setOptions] = useState(initialWords);
    const [linkedBoundaries, setLinkedBoundaries] = useState(() => new Set());

    useEffect(() => {
        const newInitialWords = scrambled.map((word, index) => ({ word, id: index }));
        setOptions(newInitialWords);
        setSelected([]);
        setLinkedBoundaries(new Set());
    }, [scrambled]);

    const handleSelectWord = (word) => {
        if (disabled) return;
        playSoundEffect('pick', { volume: 0.68, throttleMs: 40, throttleKey: 'wp-scramble-pick' });
        setSelected([...selected, word]);
        setOptions(options.filter(o => o.id !== word.id));
    };

    const boundaryKey = (left, right) => `${left.id}:${right.id}`;

    const selectedGroups = useMemo(() => {
      const groups = [];
      selected.forEach((word, index) => {
        const previous = selected[index - 1];
        if (
          previous
          && linkedBoundaries.has(boundaryKey(previous, word))
          && groups.length
        ) {
          groups[groups.length - 1].push(word);
        } else {
          groups.push([word]);
        }
      });
      return groups;
    }, [selected, linkedBoundaries]);

    const keepValidBoundaries = (boundaries, words) => {
      const valid = new Set();
      for (let index = 0; index < words.length - 1; index += 1) {
        const key = boundaryKey(words[index], words[index + 1]);
        if (boundaries.has(key)) valid.add(key);
      }
      return valid;
    };

    const handleDeselectGroup = (group) => {
        if (disabled) return;
        playSoundEffect('dropBack', { volume: 0.62, throttleMs: 40, throttleKey: 'wp-scramble-drop' });
        const groupIds = new Set(group.map(word => word.id));
        const remaining = selected.filter(word => !groupIds.has(word.id));
        setSelected(remaining);
        setOptions([...options, ...group].sort((a, b) => a.id - b.id));
        setLinkedBoundaries(current => keepValidBoundaries(current, remaining));
    };

    const handleJoinGroups = (leftGroup, rightGroup) => {
      if (disabled || captureMode) return;
      const left = leftGroup[leftGroup.length - 1];
      const right = rightGroup[0];
      setLinkedBoundaries(current => {
        const next = new Set(current);
        next.add(boundaryKey(left, right));
        return next;
      });
      playSoundEffect('pick', { volume: 0.58, throttleMs: 70, throttleKey: 'wp-scramble-join' });
    };

    const handleSplitGroup = (left, right) => {
      if (disabled || captureMode) return;
      setLinkedBoundaries(current => {
        const next = new Set(current);
        next.delete(boundaryKey(left, right));
        return next;
      });
      playSoundEffect('dropBack', { volume: 0.55, throttleMs: 70, throttleKey: 'wp-scramble-split' });
    };

    const handleSubmitClick = () => {
        if (disabled) return;
        playSoundEffect('check', { volume: 0.78, throttleMs: 120, throttleKey: 'wp-scramble-check' });
        onSubmit(selected.map(s => s.word).join(' '));
    };

    const showLabel = (s) => (typeof s === 'string' ? s.replace(/_/g, ' ') : s);
    const renderOption = (word) => {
      if (captureMode) {
        return (
          <div
            data-i18n-skip
            className="rounded-md border-2 border-blue-400 bg-white px-3 py-2 text-lg"
          >
            <CapturableText
              text={showLabel(word.word)}
              captureMode
              onCaptureWord={onCaptureWord}
              keyPrefix={`scramble-${word.id}`}
            />
          </div>
        );
      }
      return (
        <button
          data-i18n-skip
          onClick={() => handleSelectWord(word)}
          disabled={disabled}
          className="rounded-md border-2 border-blue-400 bg-white px-3 py-2 text-lg font-medium transition-all hover:scale-105 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {showLabel(word.word)}
        </button>
      );
    };

    const renderSelectedGroup = (group) => (
      <div className="inline-flex min-h-11 items-stretch overflow-hidden rounded-md border-2 border-gray-300 bg-white shadow-sm animate-fade-in">
        {group.map((word, index) => (
          <React.Fragment key={word.id}>
            {index > 0 && (
              <button
                type="button"
                onClick={() => handleSplitGroup(group[index - 1], word)}
                disabled={disabled || captureMode}
                className="group/seam inline-flex w-8 shrink-0 items-center justify-center border-x border-indigo-200 bg-indigo-50 text-indigo-700 transition-colors hover:bg-amber-50 hover:text-amber-700 disabled:cursor-not-allowed"
                title={t('Split this chunk')}
                aria-label={t('Split {left} and {right}', {
                  left: showLabel(group[index - 1].word),
                  right: showLabel(word.word),
                })}
              >
                <ScissorsIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {captureMode ? (
              <div data-i18n-skip className="flex items-center px-3 py-2 text-lg font-medium">
                <CapturableText
                  text={showLabel(word.word)}
                  captureMode
                  onCaptureWord={onCaptureWord}
                  keyPrefix={`scramble-selected-${word.id}`}
                />
              </div>
            ) : (
              <button
                type="button"
                data-i18n-skip
                onClick={() => handleDeselectGroup(group)}
                disabled={disabled}
                className="px-3 py-2 text-lg font-medium text-gray-900 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                title={t('Return this chunk to the word bank')}
              >
                {showLabel(word.word)}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>
    );

    return (
        <div>
            <div className="mb-4 flex min-h-[72px] flex-wrap items-center gap-2 rounded-md border-2 border-gray-300 bg-gray-50 p-3">
                {selected.length === 0 && <span className="text-gray-400">Build your sentence here...</span>}
                {selectedGroups.map((group, groupIndex) => (
                    <React.Fragment key={group.map(word => word.id).join('-')}>
                      {groupIndex > 0 && !captureMode && (
                        <button
                          type="button"
                          onClick={() => handleJoinGroups(selectedGroups[groupIndex - 1], group)}
                          disabled={disabled}
                          className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full border border-dashed border-indigo-300 bg-white px-2 text-xs font-semibold text-indigo-700 transition-colors hover:border-indigo-500 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                          title={t('Join these chunks')}
                          aria-label={t('Join these chunks')}
                        >
                          <LinkIcon className="h-4 w-4" aria-hidden="true" />
                          <span>{t('Join')}</span>
                        </button>
                      )}
                      {renderSelectedGroup(group)}
                    </React.Fragment>
                ))}
            </div>
            <div className="flex flex-wrap justify-center gap-3 my-4 min-h-[60px]">
                {options.map((word) => (
                    <React.Fragment key={word.id}>{renderOption(word)}</React.Fragment>
                ))}
            </div>
            {captureMode ? (
              <div className="w-full rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 text-center text-sm font-medium text-indigo-800">
                Word capture is on. Tap a word, then turn capture off to continue reordering.
              </div>
            ) : (
              <button onClick={handleSubmitClick} disabled={disabled || selected.length === 0} className="w-full bg-blue-600 text-white px-4 py-3 rounded-md font-semibold text-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                  Check
              </button>
            )}
        </div>
    );
};

export const QuestionView = ({
  question,
  onSubmit,
  isPureMode,
  inputsDisabled = false,
  // optional Chinese hint and controls for stage 3 (sentence reordering)
  cnHintText,
  cnShown = false,
  onToggleCn,
  redoNoHintActive = false,
  tier,
  sessionId,
  onAiHintUsed,
  captureMode = false,
  onCaptureWord,
}) => {
  const getQuestionTitle = () => {
    const wordToShow = isPureMode ? getCleanWord(question.word) : question.word;
    switch (question.stage) {
      case 1: return 'Infer Meaning';
      case 2: return 'Definition Q&A';
      case 3: return `Sentence Reordering: ${wordToShow}`;
      case 4: return 'Meaning Match';
      default: return 'Practice';
    }
  };

  const renderQuestionContent = () => {
    const inferMeaningExercise = question.item.exercises.find(e => e.type === 'infer_meaning');
    const reorderingExercise = question.item.exercises.find(e => e.type === 'sentence_reordering');
    const synonymExercise = question.item.exercises.find(e => e.type === 'synonym_replacement');
    const wordToShow = isPureMode ? getCleanWord(question.word) : question.word;

    switch (question.stage) {
      case 1:
        return (
          <div>
            <p className="text-lg mb-4">In this sentence, what does the highlighted word mean?</p>
            <div className="p-4 border rounded-md bg-gray-100 mb-4">
                {inferMeaningExercise?.sentence && (
                  <HighlightedSentence 
                    sentence={inferMeaningExercise.sentence} 
                    isPureMode={isPureMode}
                    targetWord={question.word}
                    captureMode={captureMode}
                    onCaptureWord={onCaptureWord}
                  />
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {question.options?.map((option, index) => (
                <button key={index} onClick={() => onSubmit(option)} disabled={inputsDisabled || captureMode} className="p-3 bg-white border-2 border-gray-300 rounded-md text-left hover:bg-blue-100 hover:border-blue-400 transition-all h-full disabled:opacity-60 disabled:cursor-not-allowed">
                  {option}
                </button>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <p className="text-lg mb-4">
              What is the meaning of{' '}
              <span data-i18n-skip>
                <CapturableText
                  text={wordToShow}
                  captureMode={captureMode}
                  onCaptureWord={onCaptureWord}
                  emphasized
                  keyPrefix="definition-target"
                />
              </span>
              ?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {question.options?.map((option, index) => (
                <button key={index} onClick={() => onSubmit(option)} disabled={inputsDisabled || captureMode} className="p-3 bg-white border-2 border-gray-300 rounded-md text-left hover:bg-blue-100 hover:border-blue-400 transition-all h-full disabled:opacity-60 disabled:cursor-not-allowed">
                  {option}
                </button>
              ))}
            </div>
          </div>
        );
      case 3:
        const scrambled = Array.isArray(question.scrambled)
          ? question.scrambled
          : (typeof (reorderingExercise?.sentence_answer) === 'string'
              ? reorderingExercise.sentence_answer.split(' ')
              : []);
        return (
          scrambled && (
            <ReorderingQuestion
              key={`reordering-${question.key}`}
              question={question}
              scrambled={scrambled}
              cnHintText={cnHintText}
              cnShown={cnShown}
              onToggleCn={onToggleCn}
              redoNoHintActive={redoNoHintActive}
              onSubmit={onSubmit}
              inputsDisabled={inputsDisabled}
              captureMode={captureMode}
              tier={tier}
              sessionId={sessionId}
              onAiHintUsed={onAiHintUsed}
              renderCaptureToken={(word, tokenId) => (
                <CapturableText
                  text={typeof word === 'string' ? word.replace(/_/g, ' ') : word}
                  captureMode
                  onCaptureWord={onCaptureWord}
                  keyPrefix={`scramble-${tokenId}`}
                />
              )}
            />
          )
        );
      case 4:
        const sentence = synonymExercise?.sentence;
        const definition = `Which word best matches: "${question.item.definition?.cn}"?`;
        return (
          <div>
            <div className="mb-4">
              <p className="text-lg font-semibold text-gray-900">Which word best matches?</p>
              <p className="mt-1 text-sm text-gray-600">
                Choose the word that best captures the highlighted meaning. The sentence may use a different form or structure.
              </p>
            </div>
            {sentence ? (
              <HighlightedSentence
                sentence={sentence}
                isPureMode={isPureMode}
                captureMode={captureMode}
                onCaptureWord={onCaptureWord}
                includeLegacyMarkers
              />
            ) : (
              <p data-i18n-skip className="text-lg mb-4">{definition}</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {question.options?.map((option, index) => (
                <button
                  key={index}
                  onClick={() => onSubmit(option)}
                  disabled={inputsDisabled || captureMode}
                  className="p-3 bg-white border-2 border-gray-300 rounded-md text-center transition-all h-full font-semibold hover:bg-blue-100 hover:border-blue-400 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isPureMode ? getCleanWord(option) : option}
                </button>
              ))}
            </div>
          </div>
        );
      default:
        return <p>Invalid practice stage.</p>;
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h4 className="font-bold text-2xl mb-4 text-center">{getQuestionTitle()}</h4>
      {renderQuestionContent()}
    </div>
  );
};

const WordCaptureToggle = ({
  enabled,
  loading,
  onToggle,
  defaultWordbookTitle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={loading}
    aria-pressed={enabled}
    className={`inline-flex min-h-9 items-center gap-2 rounded border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-wait disabled:opacity-60 ${
      enabled
        ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700'
        : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
    }`}
    title={enabled ? 'Turn word capture off' : 'Turn word capture on'}
  >
    <BookmarkIcon className="h-4 w-4" aria-hidden="true" />
    <span>{loading ? 'Loading wordbooks...' : `Word capture: ${enabled ? 'ON' : 'OFF'}`}</span>
    {enabled && defaultWordbookTitle && (
      <span data-i18n-skip className="hidden max-w-32 truncate border-l border-white/40 pl-2 sm:inline">
        {defaultWordbookTitle}
      </span>
    )}
  </button>
);

const WordCaptureDialog = ({
  selection,
  wordbooks,
  lookup,
  lookupLoading,
  lookupError,
  saving,
  error,
  onClose,
  onWordbookChange,
  onPlayPronunciation,
  onConfirm,
}) => {
  const { t } = useI18n();
  if (!selection) return null;
  const local = lookup?.local || null;
  const external = lookup?.external || null;
  const meanings = Array.isArray(external?.meanings) ? external.meanings : [];
  const headword = lookup?.headword || selection.word;
  const selectedWord = lookup?.selected_word || selection.word;
  const canSave = lookup?.in_lexilab !== false;
  const hasDictionaryContent = Boolean(local || meanings.length);
  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-black/55">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-word-title"
        className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 sm:px-7">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase text-indigo-600">Dictionary & word capture</div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h2
                id="capture-word-title"
                data-i18n-skip
                className="break-words text-3xl font-bold text-gray-950"
              >
                {headword}
              </h2>
              {lookup?.pronunciation?.phonetic && (
                <span data-i18n-skip className="text-base text-gray-500">
                  {lookup.pronunciation.phonetic}
                </span>
              )}
              <button
                type="button"
                onClick={() => onPlayPronunciation?.(
                  lookup?.pronunciation?.audio_url || '',
                  headword,
                )}
                className="rounded border border-gray-300 p-2 text-gray-700 hover:bg-gray-50"
                title="Play pronunciation"
                aria-label="Play pronunciation"
              >
                <SpeakerWaveIcon className="h-5 w-5" />
              </button>
            </div>
            {selectedWord.toLowerCase() !== headword.toLowerCase() && (
              <p className="mt-1 text-sm text-gray-500">
                <span data-i18n-skip>{selectedWord}</span>
                {' '}
                <span aria-hidden="true">→</span>
                {' '}
                <span data-i18n-skip>{headword}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50"
            title="Close"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">
          {lookupLoading && !hasDictionaryContent && (
            <div className="flex items-center gap-3 py-8 text-gray-600" role="status">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
              Looking up this word...
            </div>
          )}

          {lookupError && (
            <div role="alert" className="mb-5 border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
              {lookupError}
            </div>
          )}

          {local && (
            <section aria-labelledby="lexilab-meaning-title">
              <div className="flex flex-wrap items-center gap-2">
                <h3 id="lexilab-meaning-title" className="text-sm font-semibold uppercase text-gray-500">
                  Meaning
                </h3>
                {local.part_of_speech && (
                  <span data-i18n-skip className="rounded bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                    {local.part_of_speech}
                  </span>
                )}
              </div>
              {local.definition_cn && (
                <p data-i18n-skip className="mt-2 text-xl font-semibold leading-relaxed text-gray-950">
                  {local.definition_cn}
                </p>
              )}
              {local.definition_en && (
                <p data-i18n-skip className="mt-2 text-base leading-relaxed text-gray-700">
                  {local.definition_en}
                </p>
              )}
            </section>
          )}

          {Array.isArray(local?.examples) && local.examples.length > 0 && (
            <section className="mt-6 border-t border-gray-200 pt-5" aria-labelledby="dictionary-examples-title">
              <h3 id="dictionary-examples-title" className="text-sm font-semibold uppercase text-gray-500">
                Examples
              </h3>
              <div className="mt-3 space-y-4">
                {local.examples.map((example, index) => (
                  <div key={`${example.sentence}-${index}`}>
                    <p data-i18n-skip className="text-base leading-relaxed text-gray-900">
                      {example.sentence}
                    </p>
                    {example.translation && (
                      <p data-i18n-skip className="mt-1 text-sm text-gray-500">
                        {example.translation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {meanings.length > 0 && (
            <section className="mt-6 border-t border-gray-200 pt-5" aria-labelledby="dictionary-more-title">
              <h3 id="dictionary-more-title" className="text-sm font-semibold uppercase text-gray-500">
                More dictionary meanings
              </h3>
              <div className="mt-3 divide-y divide-gray-200">
                {meanings.map((meaning, meaningIndex) => (
                  <details key={`${meaning.part_of_speech}-${meaningIndex}`} open={meaningIndex === 0} className="py-3">
                    <summary className="cursor-pointer font-semibold text-gray-900">
                      <span data-i18n-skip>{meaning.part_of_speech || t('Other meanings')}</span>
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        {t('{count} definitions', { count: meaning.definitions.length })}
                      </span>
                    </summary>
                    <ol className="mt-3 space-y-4 pl-5">
                      {meaning.definitions.map((definition, definitionIndex) => (
                        <li key={`${definition.definition}-${definitionIndex}`} className="list-decimal pl-1">
                          <p data-i18n-skip className="leading-relaxed text-gray-800">{definition.definition}</p>
                          {definition.definition_cn && (
                            <p data-i18n-skip className="mt-1 leading-relaxed text-gray-600">
                              {definition.definition_cn}
                            </p>
                          )}
                          {definition.example && (
                            <p data-i18n-skip className="mt-1 italic leading-relaxed text-gray-500">
                              “{definition.example}”
                            </p>
                          )}
                          {definition.synonyms?.length > 0 && (
                            <p className="mt-1 text-sm text-gray-500">
                              Synonyms:
                              {' '}
                              <span data-i18n-skip>{definition.synonyms.join(', ')}</span>
                            </p>
                          )}
                        </li>
                      ))}
                    </ol>
                  </details>
                ))}
              </div>
            </section>
          )}

          {!lookupLoading && !hasDictionaryContent && (
            <div className="py-8 text-gray-600">
              No dictionary entry was found for this word.
            </div>
          )}

          <div className="mt-6 border-t border-gray-200 pt-4 text-xs text-gray-500">
            {external?.status === 'ok' ? (
              <>
                Additional definitions from
                {' '}
                {external.source_url ? (
                  <a
                    href={external.source_url}
                    target="_blank"
                    rel="noreferrer"
                    data-i18n-skip
                    className="font-medium text-indigo-700 underline hover:text-indigo-900"
                  >
                    Free Dictionary API
                  </a>
                ) : (
                  <span data-i18n-skip>Free Dictionary API</span>
                )}
                .
              </>
            ) : (
              <span>{t('Showing the LexiLamp dictionary.')}</span>
            )}
            {lookupLoading && hasDictionaryContent && (
              <span className="ml-2">Loading additional dictionary details...</span>
            )}
          </div>

          {!canSave && !lookupLoading && (
            <div className="mt-5 border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
              {t('This word can be looked up, but it is not yet available for LexiLamp wordbooks.')}
            </div>
          )}
        </div>

        <footer className="border-t border-gray-200 bg-white px-5 py-4 sm:px-7">
          <label className="block text-sm font-medium text-gray-700" htmlFor="capture-wordbook">
            Add to wordbook
          </label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <select
              id="capture-wordbook"
              value={selection.wordbookId}
              onChange={(event) => onWordbookChange(event.target.value)}
              disabled={saving || !canSave}
              className="min-w-0 flex-1 rounded border border-gray-300 bg-white p-2.5 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-100"
            >
              {wordbooks.map((book) => (
                <option data-i18n-skip key={book._id} value={book._id}>
                  {book.title} ({book.word_count})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onConfirm}
              disabled={saving || !selection.wordbookId || !canSave}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded bg-indigo-600 px-5 py-2 font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              <BookmarkIcon className="h-4 w-4" aria-hidden="true" />
              {saving ? 'Saving...' : 'Add to wordbook'}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Change the default wordbook from My Wordbooks.
          </p>
          {error && (
            <div role="alert" className="mt-3 border-l-4 border-red-400 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded border border-gray-300 bg-white px-4 py-2 font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

// Caches to keep UX stable across StrictMode remounts
const reorderingExplainCache = new Map(); // key -> explanation
const loggedReorderKeys = new Set(); // key set to avoid duplicate logs

const FeedbackModal = ({ result, onClose, isPureMode }) => {
  const { item } = result || {};

  // Hooks for reordering explanation (declare unconditionally to satisfy Hooks rules)
  const [explanation, setExplanation] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // Conditionally fetch AI explanation only for sentence reordering wrong answers
  React.useEffect(() => {
    if (result && result.type === 'reordering' && !result.correct) {
      const userAnswer = result.userAnswer || '';
      const correctAnswer = result.correctAnswer || '';
      const word = (item && item.word) || '';
      const key = `${word}|${userAnswer}|${correctAnswer}`;
      let cancelled = false;
      const run = async () => {
        // If cached, show immediately and (optionally) log once
        if (result.explanation) {
          reorderingExplainCache.set(key, result.explanation);
          if (!cancelled) {
            setExplanation(result.explanation);
            setLoading(false);
          }
        } else if (reorderingExplainCache.has(key)) {
          if (!cancelled) {
            setExplanation(reorderingExplainCache.get(key));
            setLoading(false);
          }
        } else {
          // The answer was already reviewed before progress changed. Never fire
          // a second AI request merely because explanatory text was omitted.
          const expl = 'This order is not grammatically or logically equivalent to the model answer.';
          reorderingExplainCache.set(key, expl);
          if (!cancelled) {
            setExplanation(expl);
            setLoading(false);
          }
        }
        // Log once per key (after we have explanation from cache or fetch)
        try {
          if (!loggedReorderKeys.has(key)) {
            loggedReorderKeys.add(key);
            const token = localStorage.getItem('token');
            const payload = {
              user_answer: userAnswer,
              correct_answer: correctAnswer,
              explanation: reorderingExplainCache.get(key) || 'Explanation not available.',
              word
            };
            fetch('/api/student/log-reordering-error', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify(payload)
            }).catch(()=>{});
          }
        } catch (_) {}
      };
      run();
      return () => { cancelled = true; };
    } else {
      setExplanation('');
      setLoading(false);
    }
  }, [result]);

  // Success toast for correct answers
  if (result?.correct) {
    return (
      <div className="mt-4 p-4 rounded-md text-white font-bold bg-green-600 text-center shadow">
        <div>{result.reviewAccepted ? 'Also correct — accepted after a second review.' : 'Correct!'}</div>
        {result.type === 'meaning-match' && (
          <div className="mt-3 rounded bg-green-700/60 p-3 text-left font-medium">
            <div>
              <span className="text-green-100">Best match:</span>{' '}
              <span data-i18n-skip>{result.targetWord}</span>
            </div>
            {result.naturalRewrite && (
              <div className="mt-1">
                <span className="text-green-100">Natural rewrite:</span>{' '}
                <span data-i18n-skip>{result.naturalRewrite}</span>
              </div>
            )}
          </div>
        )}
        {result.reviewFeedback && (
          <div data-i18n-skip className="mt-1 text-sm font-medium text-green-50">
            {result.reviewFeedback}
          </div>
        )}
      </div>
    );
  }

  // Special handling for sentence reordering (stage 3) wrong answers
  if (result?.type === 'reordering') {
    const userAnswer = result.userAnswer || '';
    const correctAnswer = result.correctAnswer || '';
    const fmt = (s) => (typeof s === 'string' ? s.replace(/_/g, ' ') : s);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center">
        <div className="bg-white rounded-lg shadow-2xl p-8 space-y-5 w-full max-w-2xl m-4 max-h-[90vh] flex flex-col">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sentence Reordering Explanation</h2>
            <div className="space-y-2 text-gray-800">
              <p><span className="font-semibold">Your Answer:</span> <span data-i18n-skip>{fmt(userAnswer)}</span></p>
              <p><span className="font-semibold">Correct Answer:</span> <span data-i18n-skip className="text-blue-600">{fmt(correctAnswer)}</span></p>
            </div>
          </div>
          <div className="flex-grow overflow-y-auto pr-2">
            <h3 className="font-bold text-lg mt-2">Explanation</h3>
            <div data-i18n-skip className="mt-2 p-4 bg-gray-50 border rounded min-h-[80px] whitespace-pre-wrap">
              {loading ? 'Generating explanation...' : explanation}
            </div>
          </div>
          <button onClick={onClose} className="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-semibold text-lg hover:bg-blue-700">
            OK
          </button>
        </div>
      </div>
    );
  }

  // Default wrong answer modal (stages 1/2/4)
  const wordToShow = isPureMode ? getCleanWord(item.word) : item.word;
  const correctAnswerToShow = isPureMode ? getCleanWord(result.correctAnswer) : result.correctAnswer;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-2xl p-8 space-y-5 w-full max-w-lg m-4 max-h-[90vh] flex flex-col">
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-600">{getRandomEncouragement()}</p>
          <h2 data-i18n-skip className="text-4xl font-bold mt-3 text-gray-900">{wordToShow}</h2>
          <h3 className="text-xl font-semibold mt-2 text-gray-800">Correct answer: <span data-i18n-skip className="text-blue-600">{correctAnswerToShow}</span></h3>
          {result && result.userAnswer !== undefined && (
            <p className="text-base mt-2 text-gray-700">Your answer: <span data-i18n-skip className="font-medium text-gray-900">{result.userAnswer}</span></p>
          )}
        </div>
        {item && (
          <div className="flex-grow overflow-y-auto pr-2 space-y-4 mt-4 pt-4 border-t">
            <div className="p-4 border rounded-md bg-gray-50 text-left">
              <p data-i18n-skip className="font-bold text-gray-800">{item.definition.en}</p>
              <p data-i18n-skip className="text-gray-600 mt-1">{item.definition.cn}</p>
            </div>
            <h4 className="font-bold text-lg mt-4 pt-4 border-t">Example Sentences:</h4>
            <ul className="space-y-3 list-disc pl-5">
              {item.sample_sentences?.map((s, index) => (
                 <li data-i18n-skip key={index}>
                   <p className="text-gray-800 font-medium">{s.sentence}</p>
                   <p className="text-gray-500">{s.translation}</p>
                 </li>
              ))}
            </ul>
          </div>
        )}
        <button
          onClick={() => {
            playSoundEffect('continue', { volume: 0.55 });
            onClose();
          }}
          className="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-semibold text-lg hover:bg-blue-700 mt-4"
        >
          OK
        </button>
      </div>
    </div>
  );
};

// Dictation input view extracted to top-level to avoid remount on each keystroke
const DictationInlineView = ({
  currentWord,
  item,
  meaning,
  pos,
  isFetchingAudio,
  playTts,
  dictInput,
  setDictInput,
  lastDiff,
  correctFlash,
  dictIdx,
  totalCount,
  handleDictationSubmit,
  advanceAfterWrong,
}) => {
  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-3">
        <div className="text-gray-800"><span className="font-semibold">Chinese Meaning:</span> {meaning || '-'}</div>
        <button
          onClick={() => {
            playSoundEffect('replayTick', { volume: 0.52 });
            playTts(currentWord);
          }}
          disabled={isFetchingAudio}
          className="px-3 py-1.5 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {isFetchingAudio ? 'Playing...' : 'Play Pronunciation'}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <input
          className="flex-1 border rounded px-3 py-2 text-lg"
          placeholder="Type the spelling and press Enter or Submit"
          value={dictInput}
          onChange={(e) => setDictInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !lastDiff) {
              playSoundEffect('dictSubmit', { volume: 0.62, throttleMs: 90, throttleKey: 'wp-dict-submit-key' });
              handleDictationSubmit();
              return;
            }
            if (e.key === 'Backspace') {
              playSoundEffect('dictBackspace', { volume: 0.65, throttleMs: 70, throttleKey: 'wp-dict-backspace' });
              return;
            }
            if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
              playSoundEffect('dictType', { volume: 0.65, throttleMs: 60, throttleKey: 'wp-dict-type' });
            }
          }}
          disabled={!!lastDiff}
        />
        <button
          onClick={() => {
            playSoundEffect('dictSubmit', { volume: 0.62, throttleMs: 90, throttleKey: 'wp-dict-submit-button' });
            handleDictationSubmit();
          }}
          disabled={!!lastDiff}
          className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-400"
        >
          Submit
        </button>
      </div>

      {correctFlash && <div className="mt-3 p-2 bg-green-500 text-white text-center font-bold rounded">Correct!</div>}

      {lastDiff && (
        <div className="mt-4 p-3 border rounded bg-gray-50">
          {lastDiff.el}
          <div className="mt-3 text-sm text-gray-700 space-y-2">
            {pos && <div><span className="font-semibold">POS:</span> {pos}</div>}
            {item?.definition?.en && <div><span className="font-semibold">English:</span> <span data-i18n-skip>{item.definition.en}</span></div>}
            {meaning && <div><span className="font-semibold">Chinese:</span> <span data-i18n-skip>{meaning}</span></div>}
            {Array.isArray(item?.sample_sentences) && item.sample_sentences.length > 0 && (
              <div className="mt-2">
                <div className="font-semibold">Examples:</div>
                <ul className="list-disc ml-5">
                  {item.sample_sentences.slice(0,2).map((s, i) => (
                    <li data-i18n-skip key={i}><span className="text-gray-800">{s.sentence}</span>{s.translation ? <span className="text-gray-600"> — {s.translation}</span> : null}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-4 text-right">
            <button
              onClick={advanceAfterWrong}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600 text-right">{Math.min(dictIdx + 1, totalCount)} / {totalCount}</div>
    </div>
  );
};

const QuizSession = ({
  items,
  initialProgress,
  onSessionEnd,
  onExit,
  onWordRelearned,
  isPureMode,
  setIsPureMode,
  practiceMode,
  tier,
  resumeRoundWords,
  resumeQuestionIndex,
  pretestSkipped = [],
  extraDictationWords = [],
  fullItems = [],
  onDictationComplete,
  captureMode,
  captureLoading,
  captureNotice,
  captureDefaultTitle,
  onToggleCapture,
  onCaptureWord,
  onPracticeEvent,
  sessionId,
  onStartAdaptiveReading,
  trainingMode = TRAINING_MODE_MEANING,
  customReviewActive = false,
  customReviewSelection = [],
}) => {
  const { t } = useI18n();
  const isComprehensiveTraining = normalizeTrainingMode(trainingMode) === TRAINING_MODE_COMPREHENSIVE;
  const comprehensiveItems = useMemo(
    () => (items || []).map((item) => normalizeComprehensiveItem(item)).filter((item) => item.word),
    [items],
  );
  const comprehensiveGroups = useMemo(
    () => chunkComprehensiveItems(comprehensiveItems, 9),
    [comprehensiveItems],
  );
  const firstComprehensiveGroup = useMemo(() => comprehensiveGroups[0] || [], [comprehensiveGroups]);
  const initialComprehensiveProgress = useMemo(() => {
    const hasComprehensiveProgress = Object.values(initialProgress || {}).some((entry) => entry && Object.prototype.hasOwnProperty.call(entry, 'step'));
    if (!hasComprehensiveProgress) return createComprehensiveProgress(firstComprehensiveGroup);
    return firstComprehensiveGroup.reduce((result, item) => {
      const key = comprehensiveItemKey(item);
      result[key] = initialProgress[key] || { step: 1, status: 'learning', last_result: null };
      return result;
    }, {});
  }, [firstComprehensiveGroup, initialProgress]);
  const [comprehensiveGroupIndex, setComprehensiveGroupIndex] = useState(0);
  const [comprehensiveQueue, setComprehensiveQueue] = useState(() => shuffleArray(firstComprehensiveGroup));
  const [comprehensiveQuestionIndex, setComprehensiveQuestionIndex] = useState(0);
  const [comprehensiveProgress, setComprehensiveProgress] = useState(initialComprehensiveProgress);
  const [comprehensiveFeedback, setComprehensiveFeedback] = useState(null);
  const [comprehensiveAnswer, setComprehensiveAnswer] = useState('');
  const [comprehensiveSelectedMeaning, setComprehensiveSelectedMeaning] = useState('');
  const [comprehensivePronunciationItems, setComprehensivePronunciationItems] = useState(null);
  const [comprehensiveError, setComprehensiveError] = useState('');
  const [comprehensiveMasteredCount, setComprehensiveMasteredCount] = useState(0);
  const comprehensiveProgressRef = useRef(initialComprehensiveProgress);
  const comprehensiveCompletedKeysRef = useRef(new Set());
  const comprehensiveLockedRef = useRef(false);
  const comprehensiveFinishedRef = useRef(false);

  useEffect(() => {
    comprehensiveProgressRef.current = comprehensiveProgress;
  }, [comprehensiveProgress]);

  useEffect(() => {
    if (!isComprehensiveTraining || !comprehensiveGroups.length) return;
    comprehensiveProgressRef.current = initialComprehensiveProgress;
    setComprehensiveGroupIndex(0);
    setComprehensiveQueue(shuffleArray(firstComprehensiveGroup));
    setComprehensiveQuestionIndex(0);
    setComprehensiveProgress(initialComprehensiveProgress);
    setComprehensiveFeedback(null);
    setComprehensiveAnswer('');
    setComprehensiveSelectedMeaning('');
    setComprehensivePronunciationItems(null);
    setComprehensiveError('');
    comprehensiveCompletedKeysRef.current = new Set(
      firstComprehensiveGroup
        .filter((item) => initialComprehensiveProgress[comprehensiveItemKey(item)]?.status === 'mastered')
        .map(comprehensiveItemKey),
    );
    setComprehensiveMasteredCount(comprehensiveCompletedKeysRef.current.size);
    comprehensiveLockedRef.current = false;
    comprehensiveFinishedRef.current = false;
  }, [firstComprehensiveGroup, initialComprehensiveProgress, isComprehensiveTraining, comprehensiveGroups.length]);

  const [wordProgress, setWordProgress] = useState(initialProgress);
  const [currentRound, setCurrentRound] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [lastAnswerResult, setLastAnswerResult] = useState(null);
  const [showDictationPrompt, setShowDictationPrompt] = useState(false);
  const [completionTasks, setCompletionTasks] = useState({
    reading: false,
    dictation: true,
    pronunciation: false,
  });

  // Dictation state
  const [dictationMode, setDictationMode] = useState(false);
  const [dictationWords, setDictationWords] = useState([]);
  const [dictIdx, setDictIdx] = useState(0);
  const [dictInput, setDictInput] = useState('');
  const [dictWrongSet, setDictWrongSet] = useState(new Set());
  const [isFetchingAudio, setIsFetchingAudio] = useState(false);
  const [lastDiff, setLastDiff] = useState(null);
  const [correctFlash, setCorrectFlash] = useState(false);
  const [dictationAllWords, setDictationAllWords] = useState([]);
  const dictationAssignmentKeysRef = useRef([]);
  // legacy caches no longer used; kept minimal
  const ttsBlobUrlCache = useRef(new Map());
  const playedOnceRef = useRef(new Set());

  // Auto audio for practice session
  const [autoAudioEnabled, setAutoAudioEnabled] = useState(() => getAutoAudioPref(true));
  const [ttsLoading, setTtsLoading] = useState(false);
  const [autoPanel, setAutoPanel] = useState({ show: false, okEnabled: false, word: '', item: null, progressSnapshot: null });
  const proceededRef = useRef(false);

  // Reordering (stage 3) CN hint + redo state
  const [reorderCnShown, setReorderCnShown] = useState(false);
  const [reorderAiHintUsed, setReorderAiHintUsed] = useState(false);
  const [reorderRedoActive, setReorderRedoActive] = useState(false);
  const [reorderReviewPending, setReorderReviewPending] = useState(false);
  const [reorderReviewError, setReorderReviewError] = useState('');

  const itemsByKey = useMemo(() => items.reduce((acc, item) => {
    acc[assignmentItemKey(item)] = item;
    return acc;
  }, {}), [items]);

  const fullItemsByKey = useMemo(() => (fullItems || []).reduce((acc, item) => {
    if (item && item.word) acc[assignmentItemKey(item)] = item;
    return acc;
  }, {}), [fullItems]);

  const mergedItemsByKey = useMemo(() => ({ ...fullItemsByKey, ...itemsByKey }), [fullItemsByKey, itemsByKey]);

  const getCn = (item) => {
    if (!item) return '';
    return (item.definition && (item.definition.cn || item.definition?.zh || item.definition?.ch)) || item.definition_cn || '';
  };

  // Try to extract a Chinese translation for the current sentence-reordering exercise.
  const getReorderCnHint = (q) => {
    try {
      const item = q?.item || {};
      const r = Array.isArray(item?.exercises) ? item.exercises.find(e => e && e.type === 'sentence_reordering') : null;
      if (!r) return '';
      // 1) Direct tiered or plain CN field, preferring explicit tiered translation
      if (r.sentence_answer_cn && typeof r.sentence_answer_cn === 'object') {
        const tieredCn = selectTierString(r.sentence_answer_cn, tier);
        if (tieredCn) return tieredCn;
      }
      const any = r.sentence_answer_cn || r.cn || r.translation || r.sentence_cn;
      if (typeof any === 'string' && any) return any;
      // Fallback for non-string 'any' (should be covered by selectTierString above if object)
      if (any && typeof any === 'object') {
          const vals = Object.values(any).filter(v => typeof v === 'string');
          if (vals.length > 0) return vals[0];
      }
      // 2) Try find from sample_sentences by matching the English
      const ansEn = selectTierString(r.sentence_answer, tier).replace(/_/g, ' ').trim();
      if (ansEn && Array.isArray(item.sample_sentences)) {
        const normalize = (s) => (s || '').toLowerCase().replace(/[\s]+/g, ' ').replace(/[.,/#!$%^&*;:{}=`~()\[\]"'\-]/g, '').trim();
        const ansKey = normalize(ansEn);
        for (const s of item.sample_sentences) {
          const sen = normalize(s?.sentence || '');
          if (sen && (sen === ansKey || sen.includes(ansKey) || ansKey.includes(sen))) {
            if (typeof s?.translation === 'string' && s.translation.trim()) return s.translation.trim();
          }
        }
        // If no exact match, fall back to the first available sample translation
        for (const s of item.sample_sentences) {
          if (typeof s?.translation === 'string' && s.translation.trim()) {
            return s.translation.trim();
          }
        }
      }
      // 3) Do not fallback to word-level definition here;
      // for sentence reordering, show no hint if no proper translation found.
      return '';
    } catch {
      return '';
    }
  };

  const selectTierString = (maybeTiered, fallbackTier = DEFAULT_TIER) => {
    if (!maybeTiered) return '';
    if (typeof maybeTiered === 'string') return maybeTiered;
    try {
      const t = (typeof fallbackTier === 'string' && fallbackTier) ? fallbackTier : DEFAULT_TIER;
      const normalized = String(t).startsWith('tier_') ? t : `tier_${t}`;
      const requestedIndex = TIER_ORDER_EASY_TO_HARD.indexOf(normalized);
      const index = requestedIndex >= 0 ? requestedIndex : 0;
      const fallbackIds = [
        normalized,
        ...TIER_ORDER_EASY_TO_HARD.slice(0, index).reverse(),
        ...TIER_ORDER_EASY_TO_HARD.slice(index + 1),
      ];
      const keys = [...new Set(fallbackIds.flatMap(id => [id, id.replace('tier_', 'tier')]))];
      for (const k of keys) {
        if (typeof maybeTiered[k] === 'string' && maybeTiered[k].trim()) return maybeTiered[k];
      }
      const vals = Object.values(maybeTiered).filter(v => typeof v === 'string' && v.trim());
      return vals.length > 0 ? vals[0] : '';
    } catch { return ''; }
  };

  const buildQuestionForItem = (itemKey, currentProgress) => {
    const item = itemsByKey[itemKey];
    const progress = currentProgress[itemKey];
    const question = { key: itemKey, word: item.word, stage: progress.stage, item };
    if (progress.stage === 1 || progress.stage === 2) {
      const correctAnswer = item.definition.cn;
      const distractors = generateDistractors(item, items, 3, 'definition');
      question.options = shuffleArray([correctAnswer, ...distractors]);
    } else if (progress.stage === 4) {
      const correctAnswer = item.word;
      const distractors = generateDistractors(item, items, 6, 'word');
      question.options = shuffleArray([correctAnswer, ...distractors]);
    } else if (progress.stage === 3) {
      const r = item.exercises.find(e => e.type === 'sentence_reordering');
      const ans = selectTierString(r?.sentence_answer, tier).trim();
      let tokens = ans.split(/\s+/).filter(Boolean);
      const targetForm = selectTierString(r?.target_form, tier).trim() || item.word;
      const maxChunks = tier === 'tier_6' ? 4 : 10;
      if (tokens.length > maxChunks) {
        tokens = mergeReorderTokens(tokens, targetForm, maxChunks);
      }
      question.scrambled = shuffleArray(tokens);
    }
    return question;
  };

  const buildNextRound = (currentProgress) => {
    const learningKeys = Object.keys(currentProgress).filter(key => currentProgress[key].status === 'learning');
    if (learningKeys.length === 0) {
        // End of normal practice: offer dictation
        setShowDictationPrompt(true);
        return;
    }
    const incorrectKeys = learningKeys.filter(key => currentProgress[key].last_result === 'incorrect');
    const correctKeys = learningKeys.filter(key => currentProgress[key].last_result === 'correct');
    let roundKeys = [...incorrectKeys];
    for (const key of correctKeys) {
      if (roundKeys.length < 8) roundKeys.push(key);
    }
    if (roundKeys.length === 0 && learningKeys.length > 0) {
        roundKeys = learningKeys.slice(0, 8);
    }

    const nextRound = roundKeys.map(key => buildQuestionForItem(key, currentProgress));

    setCurrentRound(shuffleArray(nextRound));
    setQuestionIndex(0);
  };

  useEffect(() => {
    if (isComprehensiveTraining) return;
    // If resume data includes explicit round order, rebuild without shuffling
    if (resumeRoundWords && resumeRoundWords.length > 0) {
      const restored = resumeRoundWords
        .filter(key => !!itemsByKey[key])
        .map(key => buildQuestionForItem(key, initialProgress));
      if (restored.length > 0) {
        setCurrentRound(restored);
        setQuestionIndex(Math.min(resumeQuestionIndex || 0, Math.max(restored.length - 1, 0)));
        return;
      }
    }
    buildNextRound(initialProgress);
  }, [initialProgress, resumeRoundWords, resumeQuestionIndex, itemsByKey, isComprehensiveTraining]);

  // Persist lightweight resume snapshot on progress changes (simplest approach)
  useEffect(() => {
    if (isComprehensiveTraining) return;
    try {
      const snapshot = {
        sessionStatus: 'learning',
        sessionId,
        practiceMode,
        tier,
        learningItems: items,
        wordProgress,
        currentRoundWords: currentRound.map(q => q.key),
        questionIndex,
        custom_review: customReviewActive,
        custom_review_selection: customReviewActive ? customReviewSelection : [],
      };
      localStorage.setItem('wp_resume_v1', JSON.stringify(snapshot));
    } catch {}
  }, [wordProgress, items, practiceMode, tier, currentRound, questionIndex, sessionId, isComprehensiveTraining, customReviewActive, customReviewSelection]);

  const proceedToNextStep = (progressOverride) => {
    setLastAnswerResult(null);
    // reset reordering hint/redo flags for next question
    setReorderCnShown(false);
    setReorderAiHintUsed(false);
    setReorderRedoActive(false);
    setReorderReviewPending(false);
    setReorderReviewError('');
    if (questionIndex < currentRound.length - 1) {
      setQuestionIndex(questionIndex + 1);
    } else {
      buildNextRound(progressOverride || wordProgress);
    }
  };

  const handleToggleReorderCn = (nextShown) => {
    if (nextShown && !reorderCnShown) {
      playSoundEffect('hintOpen', { volume: 0.6 });
    } else if (!nextShown && reorderCnShown) {
      playSoundEffect('softClick', { volume: 0.48 });
    }
    setReorderCnShown(nextShown);
  };

  const handleAnswerSubmit = async (answer) => {
    const currentQuestion = currentRound[questionIndex];
    if (!currentQuestion) return;
    // When the auto-audio meaning panel is visible, ignore further submissions
    if ((autoAudioEnabled && autoPanel.show) || reorderReviewPending) return;

    let isCorrect = false;
    let correctAnswerText = '';
    let answerForDisplay = answer;
    let reviewAccepted = false;
    let reviewFeedback = '';
    let alternativeReviewed = false;
    const reorderingExercise = currentQuestion.item.exercises.find(e => e.type === 'sentence_reordering');
    const synonymExercise = currentQuestion.item.exercises.find(e => e.type === 'synonym_replacement');

    switch (currentQuestion.stage) {
      case 1:
      case 2:
        isCorrect = answer === currentQuestion.item.definition.cn;
        correctAnswerText = currentQuestion.item.definition.cn;
        break;
      case 3:
        const normalizeReordering = (s) => {
          if (!s) return '';
          return s
            .replace(/_/g, ' ')                // treat merged blocks as spaces
            .replace(/[.,/#!$%^&*;:{}=`~()]/g, '') // strip punctuation (keep hyphen/apostrophe semantics if needed)
            .toLowerCase()
            .replace(/\s+/g, ' ')             // collapse spaces
            .trim();
        };
        const cleanUserAnswer = normalizeReordering(answer);
        const correctRaw = selectTierString(reorderingExercise.sentence_answer, tier);
        const cleanCorrectAnswer = normalizeReordering(correctRaw);
        isCorrect = cleanUserAnswer === cleanCorrectAnswer;
        correctAnswerText = correctRaw;
        answerForDisplay = answer;
        if (!isCorrect) {
          alternativeReviewed = true;
          setReorderReviewError('');
          setReorderReviewPending(true);
          try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/ai/review-reordering', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                user_answer: answerForDisplay,
                correct_answer: correctRaw,
              }),
            });
            const review = await response.json().catch(() => ({}));
            if (!response.ok || review.reviewed !== true) {
              setReorderReviewError(
                review.message || 'We could not verify this alternative order. Your progress has not changed; please try again.'
              );
              return;
            }
            isCorrect = review.accepted === true;
            reviewAccepted = isCorrect;
            reviewFeedback = review.feedback || '';
          } catch (_) {
            setReorderReviewError(
              'We could not verify this alternative order. Your progress has not changed; please try again.'
            );
            return;
          } finally {
            setReorderReviewPending(false);
          }
        }
        break;
      case 4:
        isCorrect = getCleanWord(answer).toLowerCase() === getCleanWord(currentQuestion.word).toLowerCase();
        correctAnswerText = currentQuestion.word;
        break;
      default:
        break;
    }

    // Any hint is scaffolding, not the final attempt. This applies both
    // to exact canonical answers and to alternatives accepted by AI review.
    if (
      currentQuestion.stage === 3
      && isCorrect
      && (reorderCnShown || reorderAiHintUsed)
      && !reorderRedoActive
    ) {
      try {
        setCurrentRound(prev => {
          try {
            const copy = [...prev];
            const q = { ...(copy[questionIndex] || {}) };
            const blocks = Array.isArray(q.scrambled)
              ? [...q.scrambled]
              : (reorderingExercise.sentence_answer || '').split(/\s+/);
            q.scrambled = shuffleArray(blocks);
            copy[questionIndex] = q;
            return copy;
          } catch {
            return prev;
          }
        });
      } catch {}
      playSoundEffect('challengeReset', { volume: 0.72 });
      setReorderRedoActive(true);
      setReorderCnShown(false);
      return;
    }

    const updatedProgress = { ...wordProgress };
    const currentWordProgress = updatedProgress[currentQuestion.key];
    let isNowMastered = false;

    if (isCorrect) {
      const nextStage = currentWordProgress.stage + 1;
      isNowMastered = nextStage >= 5;
      updatedProgress[currentQuestion.key] = {
        ...currentWordProgress,
        stage: nextStage,
        last_result: 'correct',
        status: isNowMastered ? 'mastered' : 'learning',
      };
      if (isNowMastered) {
        onWordRelearned(currentQuestion.item);
      }
    } else {
      updatedProgress[currentQuestion.key] = { ...currentWordProgress, stage: 1, last_result: 'incorrect' };
    }

    if (isCorrect) {
      const successSfx = getPracticeStageSuccessSfx(currentQuestion.stage);
      playSoundEffect(successSfx.name, { volume: successSfx.volume });
      if (isNowMastered) {
        playSoundEffect('sparkle', { volume: 0.78 });
      }
    } else {
      playSoundEffect('wrong', { volume: 0.74 });
    }
    setWordProgress(updatedProgress);
    if (typeof onPracticeEvent === 'function') {
      onPracticeEvent({
        event: 'attempt',
        word: currentQuestion.word,
        sense_id: currentQuestion.item?.sense_id || 'primary',
        stage: currentQuestion.stage,
        correct: isCorrect,
        used_hint: currentQuestion.stage === 3 && (reorderCnShown || reorderAiHintUsed),
        hint_type: currentQuestion.stage === 3
          ? (
            reorderCnShown && reorderAiHintUsed
              ? 'chinese_and_ai'
              : reorderAiHintUsed
                ? 'ai'
                : reorderCnShown
                  ? 'chinese'
                  : 'none'
          )
          : 'none',
        alternative_reviewed: alternativeReviewed,
        alternative_accepted: reviewAccepted,
      });
    }
    
    const baseResult = {
      correct: isCorrect,
      correctAnswer: correctAnswerText,
      item: currentQuestion.item,
      userAnswer: answerForDisplay,
      reviewAccepted,
      reviewFeedback,
      ...(currentQuestion.stage === 4 ? {
        type: 'meaning-match',
        targetWord: getCleanWord(currentQuestion.word),
        naturalRewrite: synonymExercise?.natural_rewrite || '',
      } : {}),
    };
    if (currentQuestion.stage === 3 && !isCorrect) {
      setLastAnswerResult({
        ...baseResult,
        type: 'reordering',
        userAnswer: answerForDisplay,
        explanation: reviewFeedback,
      });
    } else {
      setLastAnswerResult(baseResult);
    }

    if (isCorrect) {
      if (reviewAccepted) {
        // Let the student see that their alternative was deliberately accepted
        // before the normal auto-audio flow replaces the feedback.
        await new Promise(resolve => setTimeout(resolve, 1300));
      }
      if (!autoAudioEnabled) {
        setTimeout(() => { proceedToNextStep(updatedProgress); }, 600);
      } else {
        // Prepare inline auto-audio panel and start playback
        setLastAnswerResult(null);
        const item = currentQuestion.item;
        setAutoPanel({
          show: true,
          okEnabled: false,
          word: currentQuestion.word,
          item,
          progressSnapshot: updatedProgress,
          meaningMatch: currentQuestion.stage === 4 ? {
            targetWord: getCleanWord(currentQuestion.word),
            naturalRewrite: synonymExercise?.natural_rewrite || '',
          } : null,
        });
        (async () => {
          try {
            setTtsLoading(true);
            const wordText = getCleanWord(currentQuestion.word);
            await playTTSOnceWait(wordText, 2200, 'word');
            // Follow-up sentence depending on stage
            let follow = '';
            const infer = currentQuestion.item.exercises.find(e => e.type === 'infer_meaning');
            const syno = currentQuestion.item.exercises.find(e => e.type === 'synonym_replacement');
            if (currentQuestion.stage === 1 && infer?.sentence && isPureEnglish(infer.sentence) && !zhDominates(infer.sentence)) {
              follow = infer.sentence;
            } else if (currentQuestion.stage === 3 && reorderingExercise?.sentence_answer) {
              const cr = selectTierString(reorderingExercise.sentence_answer, tier);
              follow = String(cr || '').replace(/_/g, ' ');
            } else if (currentQuestion.stage === 4 && syno?.sentence && isPureEnglish(syno.sentence) && !zhDominates(syno.sentence)) {
              follow = syno.sentence;
            }
            if (follow) {
              await playTTSOnceWait(follow, 3200);
            }
          } catch (_) {
          } finally {
            setTtsLoading(false);
            setAutoPanel(prev => ({ ...prev, okEnabled: true }));
          }
        })();
      }
    }
  };

  const currentQuestion = currentRound[questionIndex];
  const masteredCount = Object.values(wordProgress).filter(p => p.status === 'mastered').length;
  // Progress across four stages per word: x/y where y = items.length * 4
  const totalSteps = useMemo(() => (Array.isArray(items) ? items.length * 4 : 0), [items]);
  const currentSteps = useMemo(() => {
    try {
      return Object.values(wordProgress || {}).reduce((sum, p) => {
        const stage = Math.max(1, Math.min(5, p?.stage ?? 1));
        const completed = Math.max(0, Math.min(4, stage - 1));
        return sum + completed;
      }, 0);
    } catch {
      return 0;
    }
  }, [wordProgress]);
  const progressPct = totalSteps > 0 ? Math.min(100, Math.max(0, (currentSteps / totalSteps) * 100)) : 0;

  // --- Dictation helpers ---
  const currentDictWord = dictationWords[dictIdx] || '';
  const expectedClean = (currentDictWord ? getCleanWord(currentDictWord) : '').toLowerCase();

  const completionAssignmentKeys = () => {
    const practicedKeys = Object.keys(wordProgress || {});
    const baseKeys = practiceMode === 'review' ? extraDictationWords : practicedKeys;
    return Array.from(new Set([...(baseKeys || []), ...(pretestSkipped || [])])).filter(Boolean);
  };

  const completionDictationWords = () => Array.from(new Set(
    completionAssignmentKeys().map((key) => {
      const item = mergedItemsByKey[key];
      const word = item?.word || key.split('::')[0];
      const meaning = getCn(item).trim();
      return meaning ? `${word} (${meaning})` : word;
    }).filter(Boolean),
  ));

  const startDictation = () => {
    playSoundEffect('modeEnter', { volume: 0.82 });
    // Learn mode: practiced words 鈭?pretestSkipped
    // Review mode: all review words via extraDictationWords
    const assignmentKeys = completionAssignmentKeys();
    dictationAssignmentKeysRef.current = assignmentKeys;
    const all = Array.from(new Set(assignmentKeys.map((key) => (
      mergedItemsByKey[key]?.word || key.split('::')[0]
    )).filter(Boolean)));
    setDictationWords(all);
    setDictationAllWords(all);
    setDictIdx(0);
    setDictInput('');
    setDictWrongSet(new Set());
    setLastDiff(null);
    setShowDictationPrompt(false);
    setDictationMode(true);
  };

  const finishCompletionFlow = () => {
    playSoundEffect('softClick', { volume: 0.5 });
    setShowDictationPrompt(false);
    onSessionEnd();
  };

  const startSelectedCompletionTasks = () => {
    const followupModes = [
      ...(completionTasks.dictation ? ['dictation'] : []),
      ...(completionTasks.pronunciation ? ['pronunciation'] : []),
    ];
    if (completionTasks.reading) {
      onStartAdaptiveReading?.(sessionId, {
        startReading: true,
        followupModes,
        followupWords: followupModes.length ? completionDictationWords() : [],
      });
      return;
    }
    if (followupModes.length) {
      onStartAdaptiveReading?.(sessionId, {
        startReading: false,
        followupModes,
        followupWords: completionDictationWords(),
      });
      return;
    }
    finishCompletionFlow();
  };

  const endDictation = () => {
    playSoundEffect('mastery', { volume: 0.9 });
    // Mark all dictation words as mastered and notify parent
    try {
      if (typeof onDictationComplete === 'function') {
        onDictationComplete([...(dictationAssignmentKeysRef.current || [])]);
      }
    } catch (_) {}
    onSessionEnd();
  };

  const lcsDiff = (a, b) => {
    // Simple LCS to align expected (b) vs user (a)
    const n = a.length, m = b.length;
    const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    const res = [];
    let i = 0, j = 0;
    while (i < n && j < m) {
      if (a[i] === b[j]) { res.push({ type: 'eq', ch: a[i] }); i++; j++; }
      else if (dp[i + 1][j] >= dp[i][j + 1]) { res.push({ type: 'ins', ch: a[i] }); i++; }
      else { res.push({ type: 'del', ch: b[j] }); j++; }
    }
    while (i < n) { res.push({ type: 'ins', ch: a[i++] }); }
    while (j < m) { res.push({ type: 'del', ch: b[j++] }); }
    return res;
  };

  const renderDictationDiff = (user, expected) => {
    const diff = lcsDiff(user, expected);
    // Build two lines per spec
    const userLine = [];
    const expLine = [];
    diff.forEach((d, idx) => {
      if (d.type === 'eq') {
        userLine.push(<span key={`u${idx}`} className="px-0.5">{d.ch}</span>);
        expLine.push(<span key={`e${idx}`} className="px-0.5">{d.ch}</span>);
      } else if (d.type === 'ins') {
        userLine.push(<span key={`u${idx}`} className="px-0.5 bg-red-200 text-red-900 rounded-sm">{d.ch}</span>);
        expLine.push(<span key={`e${idx}`} className="inline-block w-2"/>);
      } else if (d.type === 'del') {
        userLine.push(<span key={`u${idx}`} className="px-0.5 text-gray-400">·</span>);
        expLine.push(<span key={`e${idx}`} className="px-0.5 underline decoration-blue-500 decoration-2 underline-offset-4">{d.ch}</span>);
      }
    });
    return (
      <div className="mt-3 space-y-1">
        <div className="text-sm text-gray-700">Your Answer:</div>
        <div className="font-mono text-lg break-all">{userLine}</div>
        <div className="text-sm text-gray-700 mt-2">Correct Spelling:</div>
        <div className="font-mono text-lg break-all">{expLine}</div>
      </div>
    );
  };

  const playTts = async (word) => {
    try {
      setIsFetchingAudio(true);
      await playTTS(getCleanWord(word), 'word');
    } catch (_) {
    } finally {
      setIsFetchingAudio(false);
    }
  };

  // Auto play for dictation: when entering a new word, speak it (respects auto audio toggle)
  useEffect(() => {
    if (!dictationMode || !autoAudioEnabled) return;
    const w = currentDictWord;
    if (!w) return;
    (async () => {
      try { await playTTSOnceWait(getCleanWord(w), 2000, 'word'); } catch (_) {}
    })();
    return () => { /* nothing */ };
  }, [dictationMode, currentDictWord, autoAudioEnabled]);

  useEffect(() => {
    return () => {
      // Revoke any created blob URLs on unmount to avoid leaks
      try {
        for (const url of ttsBlobUrlCache.current.values()) {
          URL.revokeObjectURL(url);
        }
        ttsBlobUrlCache.current.clear();
      } catch {}
    };
  }, []);

  const handleDictationSubmit = () => {
    const user = (dictInput || '').trim().toLowerCase();
    const expected = expectedClean;
    if (user === expected) {
      playSoundEffect('dictCorrect', { volume: 0.86 });
      setCorrectFlash(true);
      setLastDiff(null);
      setTimeout(() => {
        setCorrectFlash(false);
        const nextIdx = dictIdx + 1;
        if (nextIdx >= dictationWords.length) {
          // end of pass
          const wrong = Array.from(dictWrongSet);
          if (wrong.length > 0) {
            playSoundEffect('retryLoop', { volume: 0.68 });
            setDictationWords(wrong);
            setDictWrongSet(new Set());
            setDictIdx(0);
            setDictInput('');
          } else {
            playSoundEffect('nextCard', { volume: 0.6 });
            endDictation();
          }
        } else {
          playSoundEffect('nextCard', { volume: 0.6 });
          setDictIdx(nextIdx);
          setDictInput('');
        }
      }, 800);
    } else {
      playSoundEffect('dictWrong', { volume: 0.74 });
      // wrong: compute diff and require acknowledge
      const diffEl = renderDictationDiff(user, expected);
      setLastDiff({ el: diffEl, word: currentDictWord });
      const newSet = new Set(dictWrongSet);
      newSet.add(currentDictWord);
      setDictWrongSet(newSet);
    }
  };

  const advanceAfterWrong = () => {
    setLastDiff(null);
    const nextIdx = dictIdx + 1;
    if (nextIdx >= dictationWords.length) {
      const wrong = Array.from(dictWrongSet);
      if (wrong.length > 0) {
        playSoundEffect('retryLoop', { volume: 0.68 });
        setDictationWords(wrong);
        setDictWrongSet(new Set());
        setDictIdx(0);
        setDictInput('');
      } else {
        playSoundEffect('nextCard', { volume: 0.6 });
        endDictation();
      }
    } else {
      playSoundEffect('nextCard', { volume: 0.6 });
      setDictIdx(nextIdx);
      setDictInput('');
    }
  };

  const handleAutoAudioToggle = () => {
    const next = !autoAudioEnabled;
    playSoundEffect(next ? 'continue' : 'muteClick', { volume: 0.52 });

    if (!next) {
      const shouldAdvance = autoPanel.show;
      const snap = autoPanel.progressSnapshot || wordProgress;

      skipAllTTS();
      setTtsLoading(false);
      setAutoPanel({ show: false, okEnabled: false, word: '', item: null, progressSnapshot: null });

      // If the answer was already accepted and we were waiting on the audio panel's OK,
      // turning auto audio off should continue the session instead of leaving the same
      // question on screen with progress already advanced.
      if (shouldAdvance) {
        proceedToNextStep(snap);
      }
    }

    setAutoAudioPref(next);
    setAutoAudioEnabled(next);
  };

  const comprehensiveGroupItems = comprehensiveGroups[comprehensiveGroupIndex] || [];
  const comprehensiveCurrent = comprehensiveQueue[comprehensiveQuestionIndex] || null;
  const comprehensiveCurrentKey = comprehensiveCurrent ? comprehensiveItemKey(comprehensiveCurrent) : '';
  const comprehensiveCurrentProgress = comprehensiveCurrent
    ? comprehensiveProgress[comprehensiveCurrentKey] || { step: 1, status: 'learning' }
    : null;
  const comprehensiveCurrentStep = Math.min(2, Math.max(1, Number(comprehensiveCurrentProgress?.step) || 1));
  const comprehensiveMeaningChoices = useMemo(
    () => comprehensiveCurrent
      ? buildComprehensiveMeaningChoices(comprehensiveCurrent, comprehensiveItems)
      : [],
    [comprehensiveCurrent, comprehensiveItems],
  );
  const comprehensiveQuestion = useMemo(
    () => comprehensiveCurrent
      ? buildComprehensiveQuestion(comprehensiveCurrent, comprehensiveMeaningChoices)
      : null,
    [comprehensiveCurrent, comprehensiveMeaningChoices],
  );
  const comprehensiveDictationChoices = useMemo(
    () => buildComprehensiveDictationChoices(comprehensiveCurrent, comprehensiveMeaningChoices),
    [comprehensiveCurrent, comprehensiveMeaningChoices],
  );

  const recordComprehensiveAttempt = (item, stage, correct, details = {}) => {
    if (typeof onPracticeEvent !== 'function') return;
    onPracticeEvent({
      event: 'attempt',
      word: item.word,
      sense_id: item.sense_id || 'primary',
      stage,
      correct,
      training_mode: TRAINING_MODE_COMPREHENSIVE,
      ...details,
    });
  };

  const persistComprehensiveCompletion = async (item) => {
    const claim = claimComprehensiveCompletion(comprehensiveCompletedKeysRef.current, item);
    if (!claim.claimed) return true;
    comprehensiveCompletedKeysRef.current = claim.keys;
    try {
      const saved = await onWordRelearned?.(item);
      if (saved === false) {
        const reverted = new Set(comprehensiveCompletedKeysRef.current);
        reverted.delete(claim.key);
        comprehensiveCompletedKeysRef.current = reverted;
        return false;
      }
      setComprehensiveMasteredCount(comprehensiveCompletedKeysRef.current.size);
      return true;
    } catch (_) {
      const reverted = new Set(comprehensiveCompletedKeysRef.current);
      reverted.delete(claim.key);
      comprehensiveCompletedKeysRef.current = reverted;
      return false;
    }
  };

  const applyComprehensiveProgress = async (item, stage, correct, details = {}, showFeedback = true) => {
    setComprehensiveError('');
    const key = comprehensiveItemKey(item);
    const currentEntry = comprehensiveProgressRef.current[key] || { step: 1, status: 'learning', last_result: null };
    const completed = correct && Number(currentEntry.step || 1) + 1 >= 4;
    if (completed && !(await persistComprehensiveCompletion(item))) {
      setComprehensiveError(t('Your progress could not be saved. Please try again.'));
      return false;
    }
    const nextProgress = updateComprehensiveProgress(comprehensiveProgressRef.current, item, correct);
    recordComprehensiveAttempt(item, stage, correct, details);
    comprehensiveProgressRef.current = nextProgress;
    setComprehensiveProgress(nextProgress);
    if (showFeedback) {
      setComprehensiveFeedback({
        correct,
        item,
        userAnswer: details.userAnswer,
        correctAnswer: details.correctAnswer || getDefinitionCn(item),
        spellingCorrect: details.spellingCorrect,
        meaningCorrect: details.meaningCorrect,
      });
    }
    return true;
  };

  const finishComprehensiveTraining = () => {
    if (comprehensiveFinishedRef.current) return;
    comprehensiveFinishedRef.current = true;
    setShowDictationPrompt(true);
  };

  const continueComprehensiveGroup = (progressSnapshot = comprehensiveProgressRef.current) => {
    const nextActivity = nextComprehensiveActivity(comprehensiveGroupItems, progressSnapshot, 3);
    comprehensiveLockedRef.current = false;
    setComprehensiveFeedback(null);
    setComprehensiveAnswer('');
    setComprehensiveSelectedMeaning('');
    setComprehensiveError('');

    if (nextActivity.kind === 'pronunciation') {
      setComprehensiveQueue([]);
      setComprehensiveQuestionIndex(0);
      setComprehensivePronunciationItems(nextActivity.items);
      return;
    }
    if (nextActivity.kind === 'practice') {
      setComprehensivePronunciationItems(null);
      setComprehensiveQueue(shuffleArray(nextActivity.items));
      setComprehensiveQuestionIndex(0);
      return;
    }
    if (comprehensiveGroupIndex + 1 < comprehensiveGroups.length) {
      const nextGroupIndex = comprehensiveGroupIndex + 1;
      const nextItems = comprehensiveGroups[nextGroupIndex] || [];
      const nextProgress = createComprehensiveProgress(nextItems);
      setComprehensiveGroupIndex(nextGroupIndex);
      setComprehensiveQueue(shuffleArray(nextItems));
      setComprehensiveQuestionIndex(0);
      comprehensiveProgressRef.current = nextProgress;
      setComprehensiveProgress(nextProgress);
      setComprehensivePronunciationItems(null);
      return;
    }
    finishComprehensiveTraining();
  };

  const advanceComprehensiveFeedback = () => {
    const progressSnapshot = comprehensiveProgressRef.current;
    setComprehensiveFeedback(null);
    setComprehensiveAnswer('');
    setComprehensiveSelectedMeaning('');
    comprehensiveLockedRef.current = false;
    if (comprehensiveQuestionIndex + 1 < comprehensiveQueue.length) {
      setComprehensiveQuestionIndex((value) => value + 1);
      return;
    }
    continueComprehensiveGroup(progressSnapshot);
  };

  useEffect(() => {
    if (!isComprehensiveTraining || !comprehensiveFeedback?.correct) return undefined;
    const timer = window.setTimeout(advanceComprehensiveFeedback, 600);
    return () => window.clearTimeout(timer);
    // The callback is intentionally captured for this answer's feedback state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comprehensiveFeedback, isComprehensiveTraining, comprehensiveQuestionIndex, comprehensiveQueue.length]);

  const submitComprehensiveMeaning = (choice) => {
    if (!comprehensiveCurrent || comprehensiveLockedRef.current || comprehensiveFeedback) return;
    comprehensiveLockedRef.current = true;
    applyComprehensiveProgress(comprehensiveCurrent, 1, choice === getDefinitionCn(comprehensiveCurrent), {
      activity_type: 'meaning_choice',
      user_answer: choice,
      correct_answer: getDefinitionCn(comprehensiveCurrent),
      userAnswer: choice,
      correctAnswer: getDefinitionCn(comprehensiveCurrent),
      meaning_correct: choice === getDefinitionCn(comprehensiveCurrent),
      meaningCorrect: choice === getDefinitionCn(comprehensiveCurrent),
    }).then((applied) => {
      if (!applied) comprehensiveLockedRef.current = false;
    });
  };

  const submitComprehensiveDictation = (event) => {
    event.preventDefault();
    if (!comprehensiveCurrent || comprehensiveLockedRef.current || comprehensiveFeedback || !comprehensiveAnswer.trim() || !comprehensiveSelectedMeaning) return;
    comprehensiveLockedRef.current = true;
    const spellingCorrect = isSpellingCorrect(comprehensiveAnswer, comprehensiveCurrent.word);
    const meaningCorrect = comprehensiveSelectedMeaning === getDefinitionCn(comprehensiveCurrent);
    applyComprehensiveProgress(comprehensiveCurrent, 2, spellingCorrect && meaningCorrect, {
      activity_type: 'dictation',
      user_answer: comprehensiveAnswer,
      correct_answer: comprehensiveCurrent.word,
      userAnswer: comprehensiveAnswer,
      correctAnswer: comprehensiveCurrent.word,
      spelling_correct: spellingCorrect,
      meaning_correct: meaningCorrect,
      spellingCorrect,
      meaningCorrect,
    }).then((applied) => {
      if (!applied) comprehensiveLockedRef.current = false;
    });
  };

  const handleComprehensivePronunciationPassed = async (item, payload) => (
    applyComprehensiveProgress(item, 3, true, {
      activity_type: 'pronunciation',
      pronunciation_score: payload?.score,
      pronunciation_feedback: payload?.feedback || payload?.summary || '',
      target_word_clear: payload?.target_word_clear,
      content_faithful: payload?.content_faithful,
      pronunciation_recording_id: payload?.recording_id,
      pronunciation_issues: payload?.issues,
      score: payload?.score,
    }, false)
  );

  const handleComprehensivePronunciationFailed = (item, payload) => {
    return applyComprehensiveProgress(item, 3, false, {
      activity_type: 'pronunciation',
      pronunciation_score: payload?.score,
      pronunciation_feedback: payload?.feedback || payload?.summary || '',
      target_word_clear: payload?.target_word_clear,
      content_faithful: payload?.content_faithful,
      pronunciation_recording_id: payload?.recording_id,
      pronunciation_issues: payload?.issues,
      score: payload?.score,
    }, false);
  };

  const handleComprehensivePronunciationRetryPassed = (item, payload) => {
    recordComprehensiveAttempt(item, 3, true, {
      activity_type: 'pronunciation',
      pronunciation_score: payload?.score,
      pronunciation_feedback: payload?.feedback || payload?.summary || '',
      pronunciation_recording_id: payload?.recording_id,
      pronunciation_issues: payload?.issues,
      target_word_clear: payload?.target_word_clear,
      content_faithful: payload?.content_faithful,
    });
    return true;
  };

  const handleComprehensivePronunciationRelearn = async (item) => {
    const key = comprehensiveItemKey(item);
    const currentEntry = comprehensiveProgressRef.current[key] || { step: 3, status: 'learning' };
    const nextProgress = {
      ...comprehensiveProgressRef.current,
      [key]: {
        ...currentEntry,
        step: 1,
        status: 'learning',
        last_result: 'relearn',
      },
    };
    comprehensiveProgressRef.current = nextProgress;
    setComprehensiveProgress(nextProgress);
    return true;
  };

  const comprehensiveTotalSteps = items.length * 3;
  const comprehensiveCompletedInCurrentGroup = completedComprehensiveCount(comprehensiveProgress);
  const comprehensiveCurrentSteps = (
    Math.max(0, comprehensiveMasteredCount - comprehensiveCompletedInCurrentGroup) * 3
    + completedComprehensiveSteps(comprehensiveProgress)
  );
  const comprehensiveProgressPct = comprehensiveTotalSteps > 0
    ? Math.min(100, Math.max(0, (comprehensiveCurrentSteps / comprehensiveTotalSteps) * 100))
    : 0;

  const handleComprehensiveBoardEmpty = () => {
    setComprehensivePronunciationItems(null);
    continueComprehensiveGroup(comprehensiveProgressRef.current);
  };

  // Prepare props for the dictation view
  const dictItem = Object.values(mergedItemsByKey).find((item) => item.word === currentDictWord) || {};
  const dictMeaning = (dictItem && dictItem.definition && dictItem.definition.cn) ? dictItem.definition.cn : '';
  const dictPos = dictItem?.pos || dictItem?.part_of_speech || '';

  const renderComprehensiveContent = () => {
    const errorBanner = comprehensiveError
      ? <div role="alert" className="mb-4 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{comprehensiveError}</div>
      : null;
    if (comprehensivePronunciationItems) {
      return (
        <>
          {errorBanner}
          <PronunciationBoard
            key={comprehensivePronunciationItems.map(comprehensiveItemKey).join('|')}
            items={comprehensivePronunciationItems}
            onExit={onExit}
            onBoardEmpty={handleComprehensiveBoardEmpty}
            onItemPassed={handleComprehensivePronunciationPassed}
            onItemFailed={handleComprehensivePronunciationFailed}
            onItemRetryPassed={handleComprehensivePronunciationRetryPassed}
            onItemRelearn={handleComprehensivePronunciationRelearn}
            allowFailedCardConfirmation
            showHeader={false}
            showGuidance={false}
            playAudio={playTTS}
            stopAudio={skipAllTTS}
          />
        </>
      );
    }
    if (comprehensiveFeedback) {
      return <>{errorBanner}<FeedbackModal result={comprehensiveFeedback} onClose={advanceComprehensiveFeedback} isPureMode={isPureMode} /></>;
    }
    if (!comprehensiveCurrent) return <>{errorBanner}<p className="text-center p-8">{t('Loading next round...')}</p></>;
    if (comprehensiveCurrentStep === 1) {
      return (
        <>
          {errorBanner}
          <QuestionView
            question={comprehensiveQuestion}
            onSubmit={submitComprehensiveMeaning}
            isPureMode={isPureMode}
            inputsDisabled={comprehensiveLockedRef.current}
            captureMode={captureMode}
            onCaptureWord={onCaptureWord}
          />
        </>
      );
    }
    return (
      <>
        {errorBanner}
        <div className="rounded-lg bg-white shadow">
          <DictationQuestion
            word={comprehensiveCurrent.word}
            choices={comprehensiveDictationChoices}
            answer={comprehensiveAnswer}
            selected={comprehensiveSelectedMeaning ? new Set([comprehensiveSelectedMeaning]) : new Set()}
            onAnswerChange={setComprehensiveAnswer}
            onToggleChoice={setComprehensiveSelectedMeaning}
            onSubmit={submitComprehensiveDictation}
            onReplay={() => playTTS(getCleanWord(comprehensiveCurrent.word), 'word')}
            inputId="comprehensive-spelling"
            multiple={false}
          />
        </div>
      </>
    );
  };

  return (
    <div className="mt-6 p-4 border-2 border-dashed border-purple-400 rounded-lg bg-purple-50">
      <div className="flex justify-between items-center mb-4">
        <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-xl font-bold text-purple-800">Practice Session</h3>
            <button
              onClick={handleAutoAudioToggle}
              className={`text-sm px-2 py-1 rounded border ${autoAudioEnabled ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200' : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'}`}
            >
              {t('Auto audio: {state}', { state: autoAudioEnabled ? t('ON') : t('OFF') })}
            </button>
            <WordCaptureToggle
              enabled={captureMode}
              loading={captureLoading}
              onToggle={onToggleCapture}
              defaultWordbookTitle={captureDefaultTitle}
            />
        </div>
        {!dictationMode && (
          <div className="text-lg font-semibold bg-green-200 text-green-800 px-3 py-1 rounded-full">
            Completed: {isComprehensiveTraining ? comprehensiveMasteredCount : masteredCount} / {items.length}
          </div>
        )}
      </div>
      {captureNotice && (
        <div
          role="status"
          className={`mb-4 rounded border px-4 py-3 text-sm ${
            captureNotice.type === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {captureNotice.message}
        </div>
      )}
      {captureMode && !dictationMode && (
        <div className="mb-4 rounded border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <span className="font-semibold">Word capture is on.</span>{' '}
          Tap any dotted English word to save it. Turn capture off when you are ready to answer.
        </div>
      )}
      {/* Hide overall progress during dictation */}
      {!dictationMode && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm text-gray-600">{isComprehensiveTraining ? `${comprehensiveCurrentSteps}/${comprehensiveTotalSteps}` : `${currentSteps}/${totalSteps}`}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div className="bg-green-500 h-3 transition-all" style={{ width: `${isComprehensiveTraining ? comprehensiveProgressPct : progressPct}%` }}></div>
          </div>
        </div>
      )}

      {isComprehensiveTraining ? renderComprehensiveContent() : <>
      
      {!dictationMode && lastAnswerResult && <FeedbackModal result={lastAnswerResult} onClose={() => proceedToNextStep()} isPureMode={isPureMode} />}
      
      {!dictationMode && !lastAnswerResult && currentQuestion && (
        <div>
          {reorderReviewPending && (
            <div
              role="status"
              className="mb-4 border border-blue-200 bg-blue-50 text-blue-900 rounded-md p-4"
            >
              <div className="font-semibold">Checking your alternative sentence...</div>
              <div className="text-sm mt-1">
                An AI reviewer is checking grammar, logic, and whether the meaning stays the same.
              </div>
            </div>
          )}
          {reorderReviewError && !reorderReviewPending && (
            <div
              role="alert"
              className="mb-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-4"
            >
              <div className="font-semibold">{t('Review unavailable — no penalty applied')}</div>
              <div className="text-sm mt-1">{reorderReviewError}</div>
            </div>
          )}
          <QuestionView
            question={currentQuestion}
            onSubmit={handleAnswerSubmit}
            isPureMode={isPureMode}
            inputsDisabled={(autoAudioEnabled && autoPanel.show) || reorderReviewPending}
            cnHintText={getReorderCnHint(currentQuestion)}
            cnShown={reorderCnShown}
            onToggleCn={handleToggleReorderCn}
            redoNoHintActive={reorderRedoActive}
            tier={tier}
            sessionId={sessionId}
            onAiHintUsed={() => setReorderAiHintUsed(true)}
            captureMode={captureMode}
            onCaptureWord={onCaptureWord}
          />
          {autoAudioEnabled && autoPanel.show && (
            <div className="mt-4 p-4 border rounded bg-white">
              {autoPanel.meaningMatch && (
                <div className="mb-3 rounded border border-green-200 bg-green-50 p-3">
                  <div className="font-semibold text-green-900">
                    Best match: <span data-i18n-skip>{autoPanel.meaningMatch.targetWord}</span>
                  </div>
                  {autoPanel.meaningMatch.naturalRewrite && (
                    <div className="mt-1 text-sm text-green-800">
                      Natural rewrite:{' '}
                      <span data-i18n-skip>{autoPanel.meaningMatch.naturalRewrite}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="text-sm text-gray-700 mb-2">Meaning:</div>
              <div data-i18n-skip className="text-gray-900 font-medium">{autoPanel.item?.definition?.cn || ''}</div>
              {autoPanel.item?.definition?.en && (
                <div data-i18n-skip className="text-gray-600 mt-1">{autoPanel.item.definition.en}</div>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  disabled={!autoPanel.okEnabled}
                  onClick={() => {
                    playSoundEffect('continue', { volume: 0.58 });
                    const snap = autoPanel.progressSnapshot || wordProgress;
                    setAutoPanel({ show: false, okEnabled: false, word: '', item: null, progressSnapshot: null });
                    proceedToNextStep(snap);
                  }}
                  className={`px-4 py-2 rounded font-semibold ${autoPanel.okEnabled ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
                >
                  OK
                </button>
                <button
                  onClick={() => {
                    playSoundEffect('muteClick', { volume: 0.52 });
                    skipAllTTS();
                    setTtsLoading(false);
                    setAutoPanel(prev => ({ ...prev, okEnabled: true }));
                  }}
                  className="px-4 py-2 rounded bg-gray-200 text-gray-900 hover:bg-gray-300"
                >
                  Skip Audio
                </button>
                <button
                  onClick={async () => {
                    playSoundEffect('replayTick', { volume: 0.55 });
                    try {
                      // Replay current sequence; do not disable OK
                      const w = getCleanWord(autoPanel.word || '');
                      if (!w) return;
                      await playTTSOnceWait(w, 2200, 'word');
                      // Try follow-up sentence while still on the same question
                      const q = currentQuestion;
                      if (q) {
                        let follow = '';
                        const infer = q.item.exercises.find(e => e.type === 'infer_meaning');
                        const reorder = q.item.exercises.find(e => e.type === 'sentence_reordering');
                        const syno = q.item.exercises.find(e => e.type === 'synonym_replacement');
                        if (q.stage === 1 && infer?.sentence && isPureEnglish(infer.sentence) && !zhDominates(infer.sentence)) {
                          follow = infer.sentence;
                        } else if (q.stage === 3 && reorder?.sentence_answer) {
                          const cr = selectTierString(reorder.sentence_answer, tier);
                          follow = String(cr || '').replace(/_/g, ' ');
                        } else if (q.stage === 4 && syno?.sentence && isPureEnglish(syno.sentence) && !zhDominates(syno.sentence)) {
                          follow = syno.sentence;
                        }
                        if (follow) await playTTSOnceWait(follow, 3200);
                      }
                    } catch (_) {}
                  }}
                  className="px-4 py-2 rounded bg-white border hover:bg-gray-50"
                >
                  Listen Again
                </button>
              </div>
              {ttsLoading && (
                <div className="mt-2 text-sm text-gray-600">{t('Speaking…')}</div>
              )}
            </div>
          )}
        </div>
      )}
      
      {!dictationMode && !lastAnswerResult && !currentQuestion && <p className="text-center p-8">Loading next round...</p>}

      {dictationMode && (
        <DictationInlineView
          currentWord={currentDictWord}
          item={dictItem}
          meaning={dictMeaning}
          pos={dictPos}
          isFetchingAudio={isFetchingAudio}
          playTts={playTts}
          dictInput={dictInput}
          setDictInput={setDictInput}
          lastDiff={lastDiff}
          correctFlash={correctFlash}
          dictIdx={dictIdx}
          totalCount={dictationWords.length}
          handleDictationSubmit={handleDictationSubmit}
          advanceAfterWrong={advanceAfterWrong}
        />
      )}

      {/* Inline auto audio panel above replaces bottom toast */}

      </>}

      {showDictationPrompt && !dictationMode && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-2xl p-8 space-y-5 w-full max-w-md m-4">
            <h2 className="text-2xl font-bold text-center">{t('Learning complete')}</h2>
            <p className="text-gray-700 text-center">{t('Choose what to practise next.')}</p>
            <div className="space-y-3">
              <label className={`flex items-start gap-3 rounded-md border p-4 cursor-pointer ${completionTasks.reading ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="checkbox"
                  checked={completionTasks.reading}
                  onChange={(event) => setCompletionTasks((current) => ({ ...current, reading: event.target.checked }))}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="flex items-center gap-2 font-semibold text-gray-900"><BookOpenIcon className="h-5 w-5 text-teal-700" /> {t('Reading practice')}</span>
                  <span className="mt-1 block text-sm text-gray-600">{t('Create a reading from the words and exercises in this session.')}</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 rounded-md border p-4 cursor-pointer ${completionTasks.dictation ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="checkbox"
                  checked={completionTasks.dictation}
                  onChange={(event) => setCompletionTasks((current) => ({ ...current, dictation: event.target.checked }))}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="flex items-center gap-2 font-semibold text-gray-900"><SpeakerWaveIcon className="h-5 w-5 text-purple-700" /> {t('Dictation')}</span>
                  <span className="mt-1 block text-sm text-gray-600">{t('Check spelling and match each word to its meaning.')}</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 rounded-md border p-4 cursor-pointer ${completionTasks.pronunciation ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input
                  type="checkbox"
                  checked={completionTasks.pronunciation}
                  onChange={(event) => setCompletionTasks((current) => ({ ...current, pronunciation: event.target.checked }))}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="flex items-center gap-2 font-semibold text-gray-900"><SpeakerWaveIcon className="h-5 w-5 text-indigo-700" /> {t('Pronunciation Practice')}</span>
                  <span className="mt-1 block text-sm text-gray-600">{t('Read short Tier 6 sentences and check whether each target word is clear.')}</span>
                </span>
              </label>
              {[completionTasks.reading, completionTasks.dictation, completionTasks.pronunciation].filter(Boolean).length > 1 && (
                <p className="text-sm text-teal-800">{t('Selected activities will open in order with the same words.')}</p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 mt-4">
              <button
                onClick={startSelectedCompletionTasks}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-indigo-700"
              >
                {t(
                  completionTasks.reading || completionTasks.dictation || completionTasks.pronunciation
                    ? 'Start selected practice'
                    : 'Finish',
                  )}
              </button>
              {(completionTasks.reading || completionTasks.dictation || completionTasks.pronunciation) && (
                <button
                  type="button"
                  onClick={finishCompletionFlow}
                  className="w-full border border-gray-300 bg-white px-6 py-3 rounded-md font-semibold text-gray-800 hover:bg-gray-50"
                >
                  {t('Finish now')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PreTestSession = ({
  items,
  onPreTestComplete,
  isPureMode,
  captureMode,
  captureLoading,
  captureNotice,
  captureDefaultTitle,
  onToggleCapture,
  onCaptureWord,
}) => {
  const { t } = useI18n();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [incorrectAnswers, setIncorrectAnswers] = useState([]);
  const [autoAudio, setAutoAudio] = useState(() => getAutoAudioPref(false));
  const [ttsLoading, setTtsLoading] = useState(false);

  const preTestQuestions = useMemo(() => {
    return shuffleDifferentOrder(items).map(item => {
      const distractors = generateDistractors(item, items, 6, 'word');
      return {
        key: assignmentItemKey(item),
        word: item.word,
        stage: 4,
        item: item,
        options: shuffleArray([item.word, ...distractors])
      };
    });
  }, [items]);

  const handleAnswerSubmit = async (answer) => {
    if (ttsLoading) return;
    const currentQuestion = preTestQuestions[questionIndex];
    const isCorrect = getCleanWord(answer).toLowerCase() === getCleanWord(currentQuestion.word).toLowerCase();
    const updatedCorrect = isCorrect ? [...correctAnswers, currentQuestion.key] : correctAnswers;
    const updatedIncorrect = !isCorrect ? [...incorrectAnswers, currentQuestion.key] : incorrectAnswers;

    setCorrectAnswers(updatedCorrect);
    setIncorrectAnswers(updatedIncorrect);

    if (autoAudio && isCorrect) {
      try {
        setTtsLoading(true);
        await playTTSOnceWait(getCleanWord(currentQuestion.word), 2400, 'word');
      } catch (_) {
      } finally {
        setTtsLoading(false);
      }
    }

    const nextIndex = questionIndex + 1;
    if (nextIndex < preTestQuestions.length) {
      setQuestionIndex(nextIndex);
    } else {
      onPreTestComplete(updatedCorrect, updatedIncorrect);
    }
  };
  
  const currentQuestion = preTestQuestions[questionIndex];
  const progress = ((questionIndex) / preTestQuestions.length) * 100;

  

  return (
    <div className="mt-6 p-4 border-2 border-dashed border-blue-400 rounded-lg bg-blue-50">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h3 className="text-xl font-bold text-blue-800">Pre-test</h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setAutoAudio(prev => { const next = !prev; setAutoAudioPref(next); return next; })}
              className={`px-2 py-1 text-sm rounded border ${autoAudio ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200' : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'}`}
            >
              {t('Auto audio: {state}', { state: autoAudio ? t('ON') : t('OFF') })}
            </button>
            <WordCaptureToggle
              enabled={captureMode}
              loading={captureLoading}
              onToggle={onToggleCapture}
              defaultWordbookTitle={captureDefaultTitle}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">{t('Let’s see which words you already know. This does not affect your final score.')}</p>
        {captureNotice && (
          <div
            role="status"
            className={`mb-4 rounded border px-4 py-3 text-sm ${
              captureNotice.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
            }`}
          >
            {captureNotice.message}
          </div>
        )}
        {captureMode && (
          <div className="mb-4 rounded border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            <span className="font-semibold">Word capture is on.</span>{' '}
            Tap a word to save it. Turn capture off to answer the pre-test.
          </div>
        )}
        
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
        </div>

        {currentQuestion ? (
            <QuestionView
              question={currentQuestion}
              onSubmit={handleAnswerSubmit}
              isPureMode={isPureMode}
              captureMode={captureMode}
              onCaptureWord={onCaptureWord}
            />
        ) : (
            <p>Loading pre-test...</p>
        )}
        {ttsLoading && (
          <div className="mt-3 text-center text-sm text-gray-600">{t('Speaking…')}</div>
        )}
    </div>
  );
};

const PreTestResultsModal = ({ knownWords, items, onConfirm, isPureMode }) => {
  const { t } = useI18n();
  const [selectedWords, setSelectedWords] = useState(() => {
    const initialState = {};
    knownWords.forEach(word => {
      initialState[word] = false; // Default to not reviewing (i.e., skipping)
    });
    return initialState;
  });

  const handleToggle = (word) => {
    setSelectedWords(prev => ({ ...prev, [word]: !prev[word] }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    knownWords.forEach(word => { allSelected[word] = true; });
    setSelectedWords(allSelected);
  };

  const handleDeselectAll = () => {
    const noneSelected = {};
    knownWords.forEach(word => { noneSelected[word] = false; });
    setSelectedWords(noneSelected);
  };

  const handleSubmit = () => {
    const wordsToKeep = Object.entries(selectedWords)
      .filter(([, isSelected]) => isSelected)
      .map(([word]) => word);
    onConfirm(wordsToKeep);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-2xl p-8 space-y-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <h2 className="text-2xl font-bold">Pre-test Complete</h2>
        <p className="text-gray-600">
          {t('The following words are judged as “mastered” and will be skipped by default. To include any of them in this session, select the corresponding words.')}
        </p>
        
        <div className="flex-grow overflow-y-auto space-y-2 pr-2">
          {knownWords.map(itemKey => {
            const item = findPracticeItem(items, itemKey);
            const word = item?.word || itemKey;
            const definition = item?.definition?.cn || item?.definition_cn || '';
            return (
            <div key={itemKey} className="flex items-center p-3 border rounded-md bg-gray-50">
              <input
                type="checkbox"
                id={`word-${itemKey}`}
                checked={selectedWords[itemKey]}
                onChange={() => handleToggle(itemKey)}
                className="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor={`word-${itemKey}`} className="ml-3 min-w-0 text-lg font-medium text-gray-800">
                <span>{isPureMode ? getCleanWord(word) : word}</span>
                {definition && <span data-i18n-skip className="ml-2 text-sm font-normal text-gray-500">· {definition}</span>}
              </label>
            </div>
          )})}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="space-x-2">
            <button onClick={handleSelectAll} className="px-4 py-2 text-sm font-semibold text-white bg-blue-500 rounded hover:bg-blue-600">Select all to include</button>
            <button onClick={handleDeselectAll} className="px-4 py-2 text-sm font-semibold text-white bg-gray-500 rounded hover:bg-gray-600">Skip all (recommended)</button>
          </div>
          <button onClick={handleSubmit} className="bg-green-600 text-white px-8 py-3 rounded-md font-bold text-lg hover:bg-green-700">
            Start learning selected words
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main Page Component ---
const WordPracticePage = () => {
  const { t, apiMessage } = useI18n();
  const [searchParams] = useSearchParams();
  const autostartHandledRef = useRef(false);
  const requestedTrainingModeRef = useRef(null);
  const autoStartStartedRef = useRef(false);
  const navigate = useNavigate();
  const [tier, setTier] = useState(null);
  const [studentTier, setStudentTier] = useState(null);
  const [studentDefaultTier, setStudentDefaultTier] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('mode-selection');
  const [practiceMode, setPracticeMode] = useState(null);
  const [trainingMode, setTrainingMode] = useState(TRAINING_MODE_MEANING);
  const [isPureMode, setIsPureMode] = useState(false);
  
  const [wordsToLearn, setWordsToLearn] = useState([]);
  const [wordsToReview, setWordsToReview] = useState([]);
  const [reviewPlan, setReviewPlan] = useState(null);
  const [desiredReviewCount, setDesiredReviewCount] = useState(1);
  const [reviewCompletedAtStart, setReviewCompletedAtStart] = useState(0);
  const [teacherAssigned, setTeacherAssigned] = useState([]);
  const [priorityWordbookId, setPriorityWordbookId] = useState('');
  const [priorityWords, setPriorityWords] = useState([]);

  const [allQuizItems, setAllQuizItems] = useState([]);
  const [learningItems, setLearningItems] = useState([]);
  const [initialProgress, setInitialProgress] = useState({});
  const [pretestSkipped, setPretestSkipped] = useState([]);
  const [extraDictationWords, setExtraDictationWords] = useState([]);
  
  const [preTestResults, setPreTestResults] = useState({ correct: [], incorrect: [] });
  const [resumeRoundWords, setResumeRoundWords] = useState(null);
  const [resumeQuestionIndex, setResumeQuestionIndex] = useState(0);
  const [activeStudySessionId, setActiveStudySessionId] = useState('');
  const [desiredLearnCount, setDesiredLearnCount] = useState(10);

  const [error, setError] = useState(null);
  const [tierFallbackCount, setTierFallbackCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingAuto, setPendingAuto] = useState(null);
  const [allowAutoStart, setAllowAutoStart] = useState(false);
  const [focusWords, setFocusWords] = useState([]);
  const [focusAssignments, setFocusAssignments] = useState([]);
  const [showCustomReview, setShowCustomReview] = useState(false);
  const [customReviewSelection, setCustomReviewSelection] = useState([]);
  const [customReviewRequested, setCustomReviewRequested] = useState(false);
  const [customReviewActive, setCustomReviewActive] = useState(false);
  const [customReviewMissing, setCustomReviewMissing] = useState(false);
  const [customReviewTotal, setCustomReviewTotal] = useState(0);
  const [customReviewDueCount, setCustomReviewDueCount] = useState(0);
  const [showPreviewList, setShowPreviewList] = useState(true);
  const [previewPage, setPreviewPage] = useState(1);
  const [audioWarmProgress, setAudioWarmProgress] = useState({ phase: 'idle', done: 0, total: 0, failed: 0 });
  const [captureMode, setCaptureMode] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [captureSettings, setCaptureSettings] = useState(null);
  const [captureSelection, setCaptureSelection] = useState(null);
  const [captureLookup, setCaptureLookup] = useState(null);
  const [captureLookupLoading, setCaptureLookupLoading] = useState(false);
  const [captureLookupError, setCaptureLookupError] = useState('');
  const [captureSaving, setCaptureSaving] = useState(false);
  const [captureError, setCaptureError] = useState('');
  const [captureNotice, setCaptureNotice] = useState(null);
  const captureLookupRequestRef = useRef(0);

  const learningModeCounts = useMemo(() => countTrainingModes(wordsToLearn), [wordsToLearn]);
  const reviewModeCounts = useMemo(
    () => getAvailableTrainingModeCounts(reviewPlan, wordsToReview),
    [reviewPlan, wordsToReview],
  );
  const modeLearningItems = useMemo(
    () => filterByTrainingMode(wordsToLearn, trainingMode),
    [trainingMode, wordsToLearn],
  );

  useEffect(() => {
    if (!allQuizItems.length || !tier) return;
    const itemKey = allQuizItems.map((item) => assignmentItemKey(item)).join('|');
    beginAudioSession(`word-practice:${activeStudySessionId || 'preview'}:${tier}:${itemKey}`);
  }, [activeStudySessionId, allQuizItems, tier]);

  useEffect(() => () => disposeAudioSession(), []);

  useEffect(() => {
    if (sessionStatus !== 'preview' || !allQuizItems.length) return undefined;
    let active = true;
    const { words, sentences } = practiceAudioRequests(allQuizItems);
    const update = (phase) => (progress) => {
      if (active) setAudioWarmProgress({ phase, ...progress });
    };
    (async () => {
      await prefetchSessionAudio(words, { concurrency: 6, onProgress: update('words') });
      await prefetchSessionAudio(sentences, { concurrency: 6, onProgress: update('sentences') });
      if (active) setAudioWarmProgress((current) => ({ ...current, phase: 'ready' }));
    })();
    return () => { active = false; };
  }, [activeStudySessionId, allQuizItems, sessionStatus, tier]);

  // Minimal persistence helpers (simple, no TTL)
  const LAST_KEY = 'wp_last_session';              // last chosen mode + tier
  const RESUME_KEY = 'wp_resume_v1';               // snapshot for active learning session
  const saveLastSession = (data) => { try { localStorage.removeItem(RESUME_KEY); localStorage.setItem(LAST_KEY, JSON.stringify(data)); } catch {} };
  const loadLastSession = () => { try { const raw = localStorage.getItem(LAST_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; } };
  const clearLastSession = () => { try { localStorage.removeItem(LAST_KEY); } catch {} };
  const saveResume = (data) => { try { localStorage.removeItem(LAST_KEY); localStorage.setItem(RESUME_KEY, JSON.stringify(data)); } catch {} };
  const loadResume = () => { try { const raw = localStorage.getItem(RESUME_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; } };
  const clearResume = () => { try { localStorage.removeItem(RESUME_KEY); } catch {} };

  const clearCustomReviewState = () => {
    clearCustomReviewSelection();
    setCustomReviewSelection([]);
    setCustomReviewRequested(false);
    setCustomReviewActive(false);
    setCustomReviewMissing(false);
    setCustomReviewTotal(0);
    setCustomReviewDueCount(0);
  };

  const persistCustomReviewState = (items, trainingModeValue, tierValue) => {
    const stored = writeCustomReviewSelection({
      items,
      trainingMode: trainingModeValue,
      tier: tierValue,
    });
    if (!stored) return false;
    setCustomReviewSelection(items);
    setCustomReviewRequested(true);
    setCustomReviewMissing(false);
    return true;
  };

  const sendStudySessionEvent = (event, sessionId = activeStudySessionId) => {
    if (!sessionId) return;
    const token = localStorage.getItem('token');
    fetch('/api/student/study-session-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        session_id: sessionId,
        training_mode: normalizeTrainingMode(event?.training_mode || trainingMode),
        ...event,
      }),
    }).catch(() => {});
  };

  const loadCaptureSettings = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/student/wordbook-capture/settings', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(apiMessage(data, 'Failed to load quick-save wordbooks.'));
    }
    const normalized = {
      defaultWordbookId: data.default_wordbook_id || '',
      wordbooks: Array.isArray(data.wordbooks) ? data.wordbooks : [],
    };
    setCaptureSettings(normalized);
    return normalized;
  };

  const handleToggleCapture = async () => {
    if (captureMode) {
      setCaptureMode(false);
      setCaptureSelection(null);
      setCaptureLookup(null);
      setCaptureLookupError('');
      setCaptureError('');
      return;
    }
    setCaptureLoading(true);
    setCaptureNotice(null);
    try {
      await loadCaptureSettings();
      setCaptureMode(true);
      playSoundEffect('bookmarkOn', { volume: 0.58 });
    } catch (captureLoadError) {
      setCaptureNotice({
        type: 'error',
        message: captureLoadError.message || t('Failed to load quick-save wordbooks.'),
      });
    } finally {
      setCaptureLoading(false);
    }
  };

  const handleCaptureWord = async (rawWord) => {
    const word = String(rawWord || '').trim();
    if (!word) return;
    setCaptureError('');
    try {
      const settings = captureSettings || await loadCaptureSettings();
      const fallbackId = settings.defaultWordbookId || settings.wordbooks[0]?._id || '';
      if (!fallbackId) throw new Error(t('No editable personal wordbook is available.'));
      setCaptureSelection({ word, wordbookId: fallbackId });
      setCaptureLookup(null);
      setCaptureLookupError('');
      setCaptureLookupLoading(true);
      playSoundEffect('softClick', { volume: 0.5 });

      const requestId = captureLookupRequestRef.current + 1;
      captureLookupRequestRef.current = requestId;
      const token = localStorage.getItem('token');
      const requestLookup = async (includeExternal) => {
        const params = new URLSearchParams({
          word,
          include_external: includeExternal ? '1' : '0',
        });
        const response = await fetch(`/api/student/dictionary-lookup?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(apiMessage(data, 'Could not look up this word.'));
        }
        return data;
      };

      try {
        const localLookup = await requestLookup(false);
        if (captureLookupRequestRef.current !== requestId) return;
        setCaptureLookup(localLookup);
        const enrichedLookup = await requestLookup(true);
        if (captureLookupRequestRef.current !== requestId) return;
        setCaptureLookup(enrichedLookup);
      } catch (lookupRequestError) {
        if (captureLookupRequestRef.current !== requestId) return;
        setCaptureLookupError(
          lookupRequestError.message || t('Could not look up this word.'),
        );
      } finally {
        if (captureLookupRequestRef.current === requestId) {
          setCaptureLookupLoading(false);
        }
      }
    } catch (captureLoadError) {
      setCaptureNotice({
        type: 'error',
        message: captureLoadError.message || t('Failed to load quick-save wordbooks.'),
      });
    }
  };

  const handleConfirmCapture = async () => {
    if (!captureSelection?.word || !captureSelection?.wordbookId || captureSaving) return;
    setCaptureSaving(true);
    setCaptureError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/student/wordbook-capture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          word: captureSelection.word,
          wordbook_id: captureSelection.wordbookId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(apiMessage(data, 'Could not save this word.'));
      }
      const savedWord = data.word || captureSelection.word;
      const title = data.wordbook_title || '';
      setCaptureSettings((previous) => {
        if (!previous || data.already_present) return previous;
        return {
          ...previous,
          wordbooks: previous.wordbooks.map((book) => (
            book._id === data.wordbook_id
              ? { ...book, word_count: Number(book.word_count || 0) + 1 }
              : book
          )),
        };
      });
      setCaptureNotice({
        type: 'success',
        message: data.already_present
          ? t('{word} is already in {wordbook}.', { word: savedWord, wordbook: title })
          : t('Saved {word} to {wordbook}.', { word: savedWord, wordbook: title }),
      });
      captureLookupRequestRef.current += 1;
      setCaptureSelection(null);
      setCaptureLookup(null);
      setCaptureLookupError('');
      setCaptureLookupLoading(false);
      playSoundEffect('bookmarkOn', { volume: 0.68 });
    } catch (captureSaveError) {
      setCaptureError(captureSaveError.message || t('Could not save this word.'));
      playSoundEffect('softFail', { volume: 0.58 });
    } finally {
      setCaptureSaving(false);
    }
  };

  useEffect(() => {
    if (!captureNotice) return undefined;
    const timer = window.setTimeout(() => setCaptureNotice(null), 4500);
    return () => window.clearTimeout(timer);
  }, [captureNotice]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("Authentication not found. Please log in again.");
        
        const summaryRes = await fetch('/api/student/dashboard-summary', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!summaryRes.ok) throw new Error('Failed to fetch your study data.');
        const summary = await summaryRes.json();
        setSummary(summary || {});
        setStudentTier(summary.minimum_tier || DEFAULT_TIER);
        setStudentDefaultTier(summary.default_tier || summary.tier || summary.minimum_tier || DEFAULT_TIER);
        const tbm = Array.isArray(summary.to_be_mastered)
          ? summary.to_be_mastered.map(normalizeAssignmentItem).filter((item) => item.word)
          : [];
        setWordsToLearn(tbm);
        setTrainingMode(resolveInitialTrainingMode(requestedTrainingModeRef.current, tbm));
        const tAssigned = Array.isArray(summary.teacher_assigned_items)
          ? summary.teacher_assigned_items.map(normalizeAssignmentItem)
          : (Array.isArray(summary.teacher_assigned) ? summary.teacher_assigned.map(normalizeAssignmentItem) : []);
        setTeacherAssigned(tAssigned);

        // Load learning preference and its TBM intersection
        try {
          const prefRes = await fetch('/api/student/learning-preference', { headers: { 'Authorization': `Bearer ${token}` } });
          const pref = await prefRes.json().catch(()=>({}));
          const wbId = pref?.priority_wordbook_id || '';
          setPriorityWordbookId(wbId || '');
          if (wbId) {
            const interRes = await fetch(`/api/student/wordbooks/${encodeURIComponent(wbId)}/tbm-words`, { headers: { 'Authorization': `Bearer ${token}` } });
            const inter = await interRes.json().catch(()=>({}));
            setPriorityWords(Array.isArray(inter?.words) ? inter.words : []);
          } else {
            setPriorityWords([]);
          }
        } catch (_) {}

        const reviewRes = await fetch('/api/student/review-words?include_senses=1', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!reviewRes.ok) throw new Error('Failed to fetch your review list.');
        const reviewWords = (await reviewRes.json()).map(normalizeAssignmentItem).filter((item) => item.word);
        // The background dashboard fetch can finish after an autostarted
        // custom batch. Never replace that explicit selection with due words.
        if (new URLSearchParams(window.location.search || '').get('custom_review') !== '1') {
          setWordsToReview(reviewWords);
        }
        const reviewPlanRes = await fetch('/api/student/review-plan', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (reviewPlanRes.ok) {
          const plan = await reviewPlanRes.json().catch(() => null);
          if (plan) {
            setReviewPlan(plan);
            setDesiredReviewCount(Math.max(1, Number(plan.default_batch_count || 1)));
          }
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Read autostart params from URL exactly once (using window.location.search snapshot)
  useEffect(() => {
    if (autostartHandledRef.current) return;
    const sp = new URLSearchParams(window.location.search || '');
    const autostart = sp.get('autostart');
    const fw = sp.get('focus_words');
    const customReviewParam = sp.get('custom_review') === '1';
    if (sp.get('focus_assignments') === 'session') {
      try {
        const stored = JSON.parse(sessionStorage.getItem('word_practice_focus_assignments') || '[]');
        const assignments = Array.isArray(stored)
          ? stored.map(normalizeAssignmentItem).filter((item) => item.word)
          : [];
        setFocusAssignments(assignments);
        sessionStorage.removeItem('word_practice_focus_assignments');
      } catch {
        setFocusAssignments([]);
      }
    }
    if (fw) {
      const list = fw.split(',').map(s => s.trim()).filter(Boolean);
      setFocusWords(list);
    }
    if (autostart === '1') {
      if (!customReviewParam) clearCustomReviewSelection();
      const mode = sp.get('mode') || 'learn';
      const tierParam = sp.get('tier');
      const trainingModeParam = sp.get('training_mode') || sp.get('trainingMode');
      const storedCustomReview = customReviewParam ? readCustomReviewSelection() : null;
      const requestedTrainingMode = trainingModeParam
        ? normalizeTrainingMode(trainingModeParam)
        : (storedCustomReview?.training_mode || null);
      const customTrainingMode = storedCustomReview?.training_mode || requestedTrainingMode || TRAINING_MODE_MEANING;
      if (customReviewParam) {
        setCustomReviewRequested(true);
        if (storedCustomReview?.items?.length) {
          setCustomReviewSelection(storedCustomReview.items);
          setCustomReviewMissing(false);
        } else {
          setCustomReviewSelection([]);
          setCustomReviewMissing(true);
          setShowCustomReview(true);
          setError(t('Custom review selection is missing. Choose the meanings again.'));
        }
      }
      requestedTrainingModeRef.current = requestedTrainingMode;
      autoStartStartedRef.current = false;
      setPracticeMode(mode);
      setTrainingMode(customReviewParam ? customTrainingMode : (requestedTrainingMode || TRAINING_MODE_MEANING));
      const customResume = customReviewParam
        ? matchingCustomReviewResume(storedCustomReview, loadResume()) : null;
      if (customResume) {
        setCustomReviewActive(true);
        setCustomReviewTotal(storedCustomReview.items.length);
        setCustomReviewDueCount(storedCustomReview.items.filter((item) => item.due).length);
        setWordsToReview(storedCustomReview.items);
        setAllQuizItems(customResume.learningItems);
        setLearningItems(customResume.learningItems);
        setInitialProgress(normalizePracticeProgress(customResume.wordProgress, customResume.learningItems));
        setActiveStudySessionId(customResume.sessionId);
        setTier(customResume.tier || storedCustomReview.tier);
        setResumeRoundWords(customResume.currentRoundWords || null);
        setResumeQuestionIndex(customResume.questionIndex || 0);
        setPendingAuto(null);
        setAllowAutoStart(false);
        autoStartStartedRef.current = true;
        autostartHandledRef.current = true;
        setSessionStatus('learning');
        return;
      }
      if (customReviewParam && !storedCustomReview?.items?.length) {
        setPendingAuto(null);
        setAllowAutoStart(false);
        setSessionStatus('mode-selection');
      } else {
        setPendingAuto(buildPendingTrainingLaunch({
          mode,
          tier: tierParam || storedCustomReview?.tier,
          trainingMode: customReviewParam ? customTrainingMode : (requestedTrainingMode || TRAINING_MODE_MEANING),
        }));
        setSessionStatus('tier-selection');
      }
      if (tierParam) setTier(tierParam);
      else if (storedCustomReview?.tier) setTier(storedCustomReview.tier);
      setAllowAutoStart(true);
      if (customReviewParam && !storedCustomReview?.items?.length) setAllowAutoStart(false);
      autostartHandledRef.current = true;
      return;
    }
    const mode = sp.get('mode');
    if (mode === 'learn' || mode === 'review') {
      const trainingModeParam = sp.get('training_mode') || sp.get('trainingMode');
      requestedTrainingModeRef.current = trainingModeParam
        ? normalizeTrainingMode(trainingModeParam)
        : null;
      setPracticeMode(mode);
      if (trainingModeParam) setTrainingMode(requestedTrainingModeRef.current);
      setSessionStatus('tier-selection');
      const t = sp.get('tier');
      if (t) setTier(t);
      setAllowAutoStart(false);
    }
    autostartHandledRef.current = true;
  }, []);

  useEffect(() => {
    if (pendingAuto && studentTier && sessionStatus !== 'loading' && !autoStartStartedRef.current) {
      const t = pendingAuto.tier || studentDefaultTier || studentTier || DEFAULT_TIER;
      autoStartStartedRef.current = true;
      if (pendingAuto.mode === 'learn') startLearnSession(t, pendingAuto.trainingMode);
      else if (pendingAuto.mode === 'review') startReviewSession(t, pendingAuto.trainingMode);
      setPendingAuto(null);
    }
  }, [pendingAuto, studentDefaultTier, studentTier, sessionStatus]);

  const availableTiers = useMemo(() => {
    // 鏁欏笀璁惧畾涓庨毦搴︽潈闄愶細
    // - tier1锛堟渶楂樻按骞筹紝琛ㄧ幇绐佸嚭锛夛細鍙兘閫?鎷旈珮 鈫?['tier_1']
    // - tier2锛堢ǔ瀹氭垚闀匡級锛氬彲閫?鍥伴毦/鎷旈珮 鈫?['tier_2','tier_1']
    // - tier3锛堥渶瑕佹敮鎸侊紝鏈€浣庢按骞筹級锛氫笁涓毦搴﹀潎鍙?鈫?['tier_3','tier_2','tier_1']
    return getAvailableTiers(studentTier || DEFAULT_TIER);
  }, [studentTier]);
  const captureDefaultTitle = captureSettings?.wordbooks?.find(
    (book) => book._id === captureSettings.defaultWordbookId
  )?.title || '';

  // Clamp desiredLearnCount to available range when data changes
  useEffect(() => {
    if (practiceMode === 'learn') {
      const total = modeLearningItems.length;
      if (total > 0) {
        const next = Math.min(Math.max(1, desiredLearnCount || 10), total);
        if (next !== desiredLearnCount) setDesiredLearnCount(next);
      }
    }
  }, [modeLearningItems, practiceMode, desiredLearnCount]);

  const handleModeSelect = (mode) => {
    if (customReviewRequested) {
      clearCustomReviewState();
      setError(null);
      const params = new URLSearchParams(window.location.search || '');
      if (params.has('custom_review')) {
        params.delete('custom_review');
        const query = params.toString();
        navigate(`/student/word-practice${query ? `?${query}` : ''}`, { replace: true });
      }
    }
    setPracticeMode(mode);
    const counts = mode === 'review' ? reviewModeCounts : learningModeCounts;
    if (Number(counts?.[trainingMode] || 0) <= 0) {
      const fallback = Number(counts?.[TRAINING_MODE_MEANING] || 0) > 0
        ? TRAINING_MODE_MEANING
        : TRAINING_MODE_COMPREHENSIVE;
      requestedTrainingModeRef.current = fallback;
      setTrainingMode(fallback);
    }
    setSessionStatus('tier-selection');
  };

  const handleTrainingModeChange = (mode) => {
    const normalized = normalizeTrainingMode(mode);
    requestedTrainingModeRef.current = normalized;
    setTrainingMode(normalized);
  };

  // 鑷姩閫夋嫨锛歵ier1锛堣〃鐜扮獊鍑猴級杩涘叆闅惧害椤垫椂鑷姩閫変腑鈥滄嫈楂樷€濓紙tier_1锛?
  useEffect(() => {
    if (sessionStatus === 'tier-selection' && !tier) {
      setTier(studentDefaultTier || studentTier || DEFAULT_TIER);
    }
  }, [sessionStatus, studentDefaultTier, studentTier, tier]);

  const updateWordReviewOnBackend = async (assignment, result) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Failed to update review status: authentication not found');
      return false;
    }

    const item = findPracticeItem(allQuizItems, assignment) || normalizeAssignmentItem(assignment);
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch('/api/student/update-word-review', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            word: item.word,
            sense_id: item.sense_id || 'primary',
            result,
            training_mode: getTrainingMode(item, trainingMode),
            ...(customReviewActive && activeStudySessionId ? { session_id: activeStudySessionId, review_source: 'custom' } : {}),
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.message || 'The review result could not be saved.');
        }
        if (payload.review_plan) setReviewPlan(payload.review_plan);
        return true;
      } catch (error) {
        if (attempt === 1) {
          console.error('Failed to update review status:', error);
          return false;
        }
      }
    }
    return false;
  };

  const masterWordsOnBackend = async (masteredAssignments, modeOverride = trainingMode) => {
    const assignments = Array.isArray(masteredAssignments) ? masteredAssignments : [masteredAssignments];
    if (assignments.length === 0) return true;
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Authentication not found");
      
      const items = assignments.map((assignment) => {
        const item = findPracticeItem(allQuizItems, assignment) || normalizeAssignmentItem(assignment);
        return {
          word: item.word,
          sense_id: item.sense_id || 'primary',
          training_mode: getTrainingMode(item, modeOverride),
        };
      }).filter((item) => item.word);
      const response = await fetch('/api/student/master-word', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ items, training_mode: normalizeTrainingMode(modeOverride) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || 'The completed word could not be saved.');
      }
      return true;
    } catch (error) {
      console.error("Failed to update mastered words on backend:", error);
      return false;
    }
  };

  // Split start flows to avoid any mode confusion
  const startLearnSession = async (tierOverride = null, trainingModeOverride = null) => {
    const tierToUse = tierOverride ?? tier;
    const modeToUse = normalizeTrainingMode(trainingModeOverride ?? trainingMode);
    if (!tierToUse) {
      setError("Please choose a difficulty level.");
      return;
    }
    requestedTrainingModeRef.current = modeToUse;
    setTrainingMode(modeToUse);
    setSessionStatus('loading');
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Authentication not found. Please log in again.");

      // Pull latest to_be_mastered list
      const summaryRes = await fetch('/api/student/dashboard-summary', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!summaryRes.ok) throw new Error('Failed to fetch your study data.');
      const summary = await summaryRes.json();
      let prioritized = [];
      if (focusAssignments && focusAssignments.length > 0) {
        prioritized = focusAssignments.map(normalizeAssignmentItem)
          .filter((item) => getTrainingMode(item) === modeToUse);
      } else if (focusWords && focusWords.length > 0) {
        prioritized = focusWords.map((word) => ({
          word: getCleanWord(word),
          sense_id: 'primary',
          training_mode: modeToUse,
        }));
      } else {
        const rawList = Array.isArray(summary.to_be_mastered)
          ? summary.to_be_mastered.map(normalizeAssignmentItem).filter((item) => item.word)
          : [];
        const teacherList = Array.isArray(summary.teacher_assigned_items)
          ? summary.teacher_assigned_items.map(normalizeAssignmentItem)
          : (Array.isArray(summary.teacher_assigned) ? summary.teacher_assigned.map(normalizeAssignmentItem) : []);
        const modeRaw = rawList.filter((item) => getTrainingMode(item) === modeToUse);
        const modeTeacherList = teacherList.filter((item) => getTrainingMode(item) === modeToUse);
        // priority: teacher first, then others
        const teacherSet = new Set(modeTeacherList.map(assignmentItemKey));
        const otherRaw = modeRaw.filter((item) => !teacherSet.has(assignmentItemKey(item)));
        // Reorder others by priority words (if set)
        const pset = new Set(priorityWords.map(getCleanWord));
        const otherList = otherRaw
          .map((item, idx) => ({ item, idx, s: pset.has(getCleanWord(item.word)) ? 1 : 0 }))
          .sort((a,b)=> (b.s - a.s) || (a.idx - b.idx))
          .map(x=>x.item);
        prioritized = [...modeTeacherList, ...otherList];
      }

      const totalCount = prioritized.length;
      if (totalCount === 0) {
        setError("No words to practice.");
        setSessionStatus('mode-selection');
        return;
      }

      // If focus_words present, use them all; otherwise respect selected count
      const selectedAssignments = ((focusAssignments && focusAssignments.length > 0) || (focusWords && focusWords.length > 0))
        ? prioritized
        : prioritized.slice(0, Math.max(1, Math.min(Number.isFinite(desiredLearnCount) ? desiredLearnCount : 10, totalCount)));
      const wordList = [];
      const seenAssignments = new Set();
      selectedAssignments.forEach((assignment) => {
        const item = normalizeAssignmentItem(assignment);
        const key = assignmentItemKey(item);
        if (item.word && !seenAssignments.has(key)) {
          wordList.push(item);
          seenAssignments.add(key);
        }
      });

      const studySessionId = createStudySessionId();
      const items = await fetchPracticeSessionData(wordList, tierToUse, token, {
        mode: 'learn',
        session_id: studySessionId,
        training_mode: modeToUse,
      });
      setActiveStudySessionId(studySessionId);
      setTierFallbackCount(items.filter(item => item.uses_tier_fallback).length);
      setWordsToLearn(items.map(normalizeAssignmentItem));

      const returnedWords = new Set(items.map(item => item.word.toLowerCase()));
      const wordsToClean = wordList
        .filter((item) => !returnedWords.has(item.word.toLowerCase()))
        .map((item) => item.word);
      if (wordsToClean.length > 0) {
        console.log("Cleaning up non-existent words:", wordsToClean);
        wordsToClean.forEach(word => {
          fetch('/api/student/word/cleanup', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ word: word }),
          });
        });
      }

      const transformedItems = items.map((item) => ({
        ...item,
        word: item.word,
        word_root: item.word_root,
        sense_id: item.sense_id || 'primary',
        sense_status: item.sense_status || 'published',
        definition: { cn: item.definition_cn, en: item.definition_en },
        sample_sentences: item.sample_sentences,
        exercises: item.exercises,
        pronunciation_sentence: item.pronunciation_sentence,
        training_mode: getTrainingMode(item, modeToUse),
      }));

      setAllQuizItems(transformedItems);
      // 杩涘叆棰勪範椤碉紙鍒楄〃 + 闂崱锛夛紝鐢辩敤鎴风偣鍑烩€滈涔犲畬鎴愨€濆啀杩涘叆娴嬪墠璇勪及
      setSessionStatus('preview');
      saveLastSession({ practiceMode: 'learn', trainingMode: modeToUse, tier: tierToUse });
    } catch (err) {
      setError(err.message);
      setSessionStatus('tier-selection');
    }
  };

  const startReviewSession = async (tierOverride = null, trainingModeOverride = null, customSelectionOverride = undefined) => {
    const tierToUse = tierOverride ?? tier;
    const modeToUse = normalizeTrainingMode(trainingModeOverride ?? trainingMode);
    if (!tierToUse) {
      setError("Please choose a difficulty level.");
      return;
    }
    requestedTrainingModeRef.current = modeToUse;
    setTrainingMode(modeToUse);
    setSessionStatus('loading');
    setError(null);
    const customSelection = customSelectionOverride !== undefined
      ? customSelectionOverride
      : (customReviewRequested ? customReviewSelection : null);
    const isCustomReview = customSelection !== null;
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Authentication not found. Please log in again.");

      if (isCustomReview) {
        const wordList = (Array.isArray(customSelection) ? customSelection : [])
          .map(normalizeAssignmentItem)
          .filter((item) => item.word && getTrainingMode(item, modeToUse) === modeToUse)
          .filter((item, index, values) => values.findIndex((candidate) => assignmentItemKey(candidate) === assignmentItemKey(item)) === index);
        if (!wordList.length) {
          throw new Error(t('Custom review selection is empty. Choose the meanings again.'));
        }
        const savedCustom = customSelectionOverride === undefined ? readCustomReviewSelection() : null;
        const studySessionId = savedCustom?.session_id || createStudySessionId();
        const items = await fetchPracticeSessionData(wordList, tierToUse, token, {
          mode: 'review',
          session_id: studySessionId,
          training_mode: modeToUse,
          review_source: 'custom',
        });
        if (!Array.isArray(items) || items.length === 0) {
          throw new Error(t('The selected meanings were not available. Please retry custom review.'));
        }
        const returnedByKey = new Map(items.map((item) => [assignmentItemKey(item), item]));
        const confirmedSelection = wordList.map((item) => ({
          ...item, due: Boolean(returnedByKey.get(assignmentItemKey(item))?.due_at_creation),
        }));
        writeCustomReviewSelection({ items: confirmedSelection, trainingMode: modeToUse, tier: tierToUse, sessionId: studySessionId });
        setCustomReviewSelection(confirmedSelection);
        setCustomReviewActive(true);
        setCustomReviewTotal(wordList.length);
        setCustomReviewDueCount(confirmedSelection.filter((item) => item.due).length);
        setActiveStudySessionId(studySessionId);
        setTierFallbackCount(items.filter((item) => item.uses_tier_fallback).length);
        setWordsToReview(confirmedSelection);
        const transformedItems = items.map((item) => ({
          ...item,
          word: item.word,
          word_root: item.word_root,
          sense_id: item.sense_id || 'primary',
          sense_status: item.sense_status || 'published',
          definition: { cn: item.definition_cn, en: item.definition_en },
          sample_sentences: item.sample_sentences,
          exercises: item.exercises,
          pronunciation_sentence: item.pronunciation_sentence,
          training_mode: getTrainingMode(item, modeToUse),
        }));
        setAllQuizItems(transformedItems);
        setSessionStatus('preview');
        saveLastSession({
          practiceMode: 'review',
          trainingMode: modeToUse,
          tier: tierToUse,
          custom_review: true,
          custom_review_selection: confirmedSelection,
        });
        return;
      }

      const requestedCount = Math.max(
        1,
        Number.parseInt(searchParams.get('review_count') || desiredReviewCount, 10) || 1,
      );
      // Pull only this batch. The rest stays safely in the persistent queue.
      const reviewQuery = new URLSearchParams({
        include_senses: '1',
        limit: String(requestedCount),
        training_mode: modeToUse,
      });
      const reviewRes = await fetch(`/api/student/review-words?${reviewQuery.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!reviewRes.ok) throw new Error('Failed to fetch your review list.');
      const reviewItems = (await reviewRes.json())
        .map(normalizeAssignmentItem)
        .filter((item) => item.word && getTrainingMode(item) === modeToUse);
      setWordsToReview(reviewItems);
      const planRes = await fetch('/api/student/review-plan', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (planRes.ok) {
        const currentPlan = await planRes.json().catch(() => null);
        if (currentPlan) {
          setReviewPlan(currentPlan);
          setReviewCompletedAtStart(Number(currentPlan.completed_today || 0));
        }
      } else {
        setReviewCompletedAtStart(Number(reviewPlan?.completed_today || 0));
      }

      const wordList = reviewItems;
      if (wordList.length === 0) {
        setError("No words to practice.");
        setSessionStatus('mode-selection');
        return;
      }

      const studySessionId = createStudySessionId();
      const items = await fetchPracticeSessionData(wordList, tierToUse, token, {
        mode: 'review',
        session_id: studySessionId,
        training_mode: modeToUse,
      });
      setActiveStudySessionId(studySessionId);
      setTierFallbackCount(items.filter(item => item.uses_tier_fallback).length);

      const returnedWords = new Set(items.map(item => item.word.toLowerCase()));
      const wordsToClean = wordList
        .filter((item) => !returnedWords.has(item.word.toLowerCase()))
        .map((item) => item.word);
      if (wordsToClean.length > 0) {
        console.log("Cleaning up non-existent words:", wordsToClean);
        wordsToClean.forEach(word => {
          fetch('/api/student/word/cleanup', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ word: word }),
          });
        });
      }

      const transformedItems = items.map((item) => ({
        ...item,
        word: item.word,
        word_root: item.word_root,
        sense_id: item.sense_id || 'primary',
        sense_status: item.sense_status || 'published',
        definition: { cn: item.definition_cn, en: item.definition_en },
        sample_sentences: item.sample_sentences,
        exercises: item.exercises,
        pronunciation_sentence: item.pronunciation_sentence,
        training_mode: getTrainingMode(item, modeToUse),
      }));

      setAllQuizItems(transformedItems);
      setSessionStatus('preview');
      saveLastSession({ practiceMode: 'review', trainingMode: modeToUse, tier: tierToUse });
    } catch (err) {
      setError(err.message);
      if (isCustomReview) {
        setCustomReviewActive(false);
        setSessionStatus('mode-selection');
      } else {
        setSessionStatus('tier-selection');
      }
    }
  };

  // 浠呭湪鏄庣‘鍏佽鑷姩寮€濮嬫椂锛圲RL 甯?autostart=1锛夋墠鑷姩寮€缁?
  const handleCustomReviewStart = ({ items, trainingMode: selectedTrainingMode, tier: selectedTier }) => {
    const normalizedMode = normalizeTrainingMode(selectedTrainingMode);
    if (!persistCustomReviewState(items, normalizedMode, selectedTier)) {
      setError(t('Custom review could not be saved. Please try again.'));
      return;
    }
    setPracticeMode('review');
    setTrainingMode(normalizedMode);
    setTier(selectedTier || studentDefaultTier || studentTier || DEFAULT_TIER);
    setShowCustomReview(false);
    const params = new URLSearchParams(window.location.search || '');
    params.set('mode', 'review');
    params.set('tier', selectedTier || studentDefaultTier || studentTier || DEFAULT_TIER);
    params.set('training_mode', normalizedMode);
    params.set('autostart', '1');
    params.set('custom_review', '1');
    navigate(`/student/word-practice?${params.toString()}`, { replace: true });
    startReviewSession(selectedTier, normalizedMode, items);
  };

  useEffect(() => {
    if (
      allowAutoStart &&
      !pendingAuto &&
      !autoStartStartedRef.current &&
      sessionStatus === 'tier-selection' &&
      studentTier === 'tier_1' &&
      tier === 'tier_1' &&
      (practiceMode === 'learn' || practiceMode === 'review')
    ) {
      autoStartStartedRef.current = true;
      setAllowAutoStart(false);
      const modeToUse = requestedTrainingModeRef.current || trainingMode;
      if (practiceMode === 'learn') startLearnSession('tier_1', modeToUse);
      else startReviewSession('tier_1', modeToUse);
    }
  }, [allowAutoStart, pendingAuto, sessionStatus, studentTier, tier, practiceMode, trainingMode]);

  const handlePreTestComplete = async (correct, incorrect) => {
    const resultFor = (itemKey, isCorrect) => {
      const item = findPracticeItem(allQuizItems, itemKey);
      return item?.word ? {
        word: item.word,
        sense_id: item.sense_id || 'primary',
        correct: isCorrect,
      } : null;
    };
    const pretestResults = [
      ...correct.map(itemKey => resultFor(itemKey, true)),
      ...incorrect.map(itemKey => resultFor(itemKey, false)),
    ].filter(Boolean);
    if (pretestResults.length > 0) {
      sendStudySessionEvent({ event: 'pretest_completed', results: pretestResults });
    }
    if (practiceMode === 'review') {
      // A correct pre-test answer completes today's review immediately. An
      // incorrect answer remains due until the learner finishes all four steps.
      await Promise.all(
        reviewResultsFromPretest(correct, incorrect)
          .map(({ assignment, result }) => updateWordReviewOnBackend(assignment, result)),
      );
      const allReview = (customReviewActive ? customReviewSelection : wordsToReview || []).map(assignmentItemKey);
      setExtraDictationWords(allReview);
      if (incorrect.length > 0) {
        // Learn the incorrect first, then dictation will include all
        handleStartLearningSession(incorrect, []);
      } else {
        // Skip relearning and go straight to dictation pane via a lightweight learning session
        // Initialize a minimal learning session so QuizSession can render and immediately prompt dictation
        const progress = {};
        allReview.forEach(key => { progress[key] = { stage: 5, status: 'mastered', last_result: 'correct' }; });
        setLearningItems(allQuizItems.filter(it => allReview.includes(assignmentItemKey(it))));
        setInitialProgress(progress);
        setSessionStatus('learning');
      }
    } else { // 'learn' mode
      setPreTestResults({ correct, incorrect });
      if (correct.length > 0) {
        setSessionStatus('pre-test-results');
      } else {
        handleStartLearningSession(incorrect, []);
      }
    }
  };

  const handleStartLearningSession = (itemsToLearn, itemsToReview = []) => {
    const finalItemKeys = [...new Set([...itemsToLearn, ...itemsToReview])];
    const itemsForLearning = allQuizItems.filter(item => finalItemKeys.includes(assignmentItemKey(item)));
    const progress = createPracticeProgress(itemsForLearning);
    setLearningItems(itemsForLearning);
    setInitialProgress(progress);
    setSessionStatus('learning');

    // Save a snapshot for resume
    saveResume({
      sessionStatus: 'learning',
       sessionId: activeStudySessionId,
       practiceMode,
       trainingMode,
       tier,
       custom_review: customReviewActive || customReviewRequested,
       custom_review_selection: customReviewActive || customReviewRequested ? customReviewSelection : [],
       learningItems: itemsForLearning,
      wordProgress: progress,
      currentRoundWords: null,
      questionIndex: 0,
    });

    // Clear any previous resume order state in UI
    setResumeRoundWords(null);
    setResumeQuestionIndex(0);
  };

  const handleConfirmPreTestChoices = (itemsToKeep) => {
    if (practiceMode === 'learn') {
      const itemsToSkip = preTestResults.correct.filter(itemKey => !itemsToKeep.includes(itemKey));
      if (itemsToSkip.length > 0) {
        masterWordsOnBackend(itemsToSkip);
        const skipped = new Set(itemsToSkip);
        setWordsToLearn(prev => prev.filter(item => !skipped.has(assignmentItemKey(item))));
      }
      setPretestSkipped(itemsToSkip);
    }
    handleStartLearningSession(preTestResults.incorrect, itemsToKeep);
  };

  const handleSessionEnd = () => {
    sendStudySessionEvent({ event: 'completed' });
    if (customReviewActive) clearCustomReviewSelection();
    setSessionStatus('finished');
    clearLastSession();
    clearResume();
    setResumeRoundWords(null);
    setResumeQuestionIndex(0);
    setActiveStudySessionId('');
  };

  useEffect(() => {
    if (sessionStatus !== 'finished' || practiceMode !== 'review') return;
    let active = true;
    const refreshReviewPlan = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/student/review-plan', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const plan = await response.json().catch(() => null);
        if (active && response.ok && plan) setReviewPlan(plan);
      } catch (_) {}
    };
    refreshReviewPlan();
    const timer = window.setTimeout(refreshReviewPlan, 800);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [sessionStatus, practiceMode]);

  // On mount, offer resume. Prefer in-progress learning snapshot; fallback to last session params.
  useEffect(() => {
    if (sessionStatus !== 'mode-selection') return;
    // A Dashboard launch is an explicit new assignment session. Do not let a
    // stale resume snapshot replace the newly selected meanings.
    const launchParams = new URLSearchParams(window.location.search || '');
    if (launchParams.get('autostart') === '1') return;

    const resume = loadResume();
    if (resume && resume.sessionStatus === 'learning' && Array.isArray(resume.learningItems) && resume.wordProgress) {
      const ok = window.confirm('Detected ongoing progress. Resume?');
      if (ok) {
         const resumedItems = resume.learningItems || [];
         const resumedProgress = normalizePracticeProgress(resume.wordProgress, resumedItems);
          if (resume.custom_review === true && Array.isArray(resume.custom_review_selection) && resume.custom_review_selection.length > 0) {
            setCustomReviewSelection(resume.custom_review_selection);
            setCustomReviewRequested(true);
            setCustomReviewActive(true);
            setCustomReviewMissing(false);
            setCustomReviewTotal(resume.custom_review_selection.length);
            setCustomReviewDueCount(resume.custom_review_selection.filter((item) => (
              item?.due === true || item?.due === 1 || item?.due === '1'
            )).length);
            writeCustomReviewSelection({
              items: resume.custom_review_selection,
              trainingMode: resume.trainingMode,
             tier: resume.tier,
           });
         }
          setPracticeMode(resume.practiceMode || null);
         setTrainingMode(normalizeTrainingMode(resume.trainingMode));
        setTier(resume.tier || null);
        setLearningItems(resumedItems);
        setInitialProgress(resumedProgress);
        setActiveStudySessionId(resume.sessionId || '');
        setSessionStatus('learning');
        if (Array.isArray(resume.currentRoundWords) && resume.currentRoundWords.length > 0) {
          setResumeRoundWords(resume.currentRoundWords.map((value) => {
            const item = findPracticeItem(resumedItems, value);
            return item ? assignmentItemKey(item) : value;
          }));
          setResumeQuestionIndex(resume.questionIndex || 0);
        } else {
          setResumeRoundWords(null);
          setResumeQuestionIndex(0);
        }
        return; // do not check LAST_KEY if learning resume exists
      } else {
        clearResume();
      }
    }

    const last = loadLastSession();
    if (!last) return;
    const ok = window.confirm('Unfinished practice detected. Restore last settings and restart?');
    if (ok) {
      if (last.practiceMode === 'learn') {
        clearCustomReviewState();
        setTrainingMode(normalizeTrainingMode(last.trainingMode));
        startLearnSession(last.tier, last.trainingMode);
      } else if (last.practiceMode === 'review') {
        const lastCustomSelection = last.custom_review === true && Array.isArray(last.custom_review_selection)
          ? last.custom_review_selection
          : null;
        if (lastCustomSelection) {
          setCustomReviewSelection(lastCustomSelection);
          setCustomReviewRequested(true);
          setCustomReviewMissing(false);
          writeCustomReviewSelection({ items: lastCustomSelection, trainingMode: last.trainingMode, tier: last.tier });
        } else {
          clearCustomReviewState();
        }
        setTrainingMode(normalizeTrainingMode(last.trainingMode));
        startReviewSession(last.tier, last.trainingMode, lastCustomSelection);
      }
      // Reflect into state for UI only
       setPracticeMode(last.practiceMode || null);
       setTrainingMode(normalizeTrainingMode(last.trainingMode));
      setTier(last.tier || null);
    } else {
      clearLastSession();
    }
  }, [sessionStatus]);
  
  // One-click from WordPractice: TBM (teacher-first) + supplement from priority wordbook
  const handleOneClickLearnHere = async () => {
    try {
      const usedLearned = (typeof summary?.secret_today_learned === 'number') ? summary.secret_today_learned : 0;
      const remaining = Math.max(0, (summary?.learning_goal || 0) - usedLearned);
      const tbm = Array.isArray(summary?.to_be_mastered) ? summary.to_be_mastered : [];
      const tbmWords = tbm.map(x => (typeof x === 'string' ? x : x.word)).filter(Boolean);
      const teacherSet = new Set((summary?.teacher_assigned || []).map(w => w));
      const teacherFirst = []; const others = [];
      for (const w of tbmWords) { if (teacherSet.has(w)) teacherFirst.push(w); else others.push(w); }
      const orderedTBM = [...teacherFirst, ...others];
      const tbmCount = (summary?.has_teacher === true) ? orderedTBM.length : 0;
      const desired = tbmCount + remaining;
      if (desired <= 0) return; // nothing to do
      const base = orderedTBM.slice(0, desired);
      let combined = [...base];
      const need = Math.max(0, desired - combined.length);
      if (need > 0) {
        // Supplement from priorityWords fetched via preference
        const sup = (priorityWords || []).filter(Boolean).filter(w => !new Set(combined).has(w));
        combined = [...combined, ...sup.slice(0, need)];
      }
      if (combined.length === 0) return;
      const params = new URLSearchParams();
      params.set('mode', 'learn');
      params.set('tier', tier || studentTier || summary?.tier || DEFAULT_TIER);
      params.set('autostart', '1');
      params.set('focus_words', combined.join(','));
      navigate(`/student/word-practice?${params.toString()}`);
    } catch (_) {}
  };

  const renderContent = () => {
    const openCustomReviewSelector = () => {
      if (customReviewRequested) clearCustomReviewState();
      setError(null);
      setShowCustomReview(true);
    };
    // 椤堕儴蹇嵎涓€閿涔犳寜閽紝鏄剧ず鍓╀綑涓庡竷缃?
    const QuickButton = () => {
      const usedLearned = (typeof summary?.secret_today_learned === 'number') ? summary.secret_today_learned : 0;
      const remaining = Math.max(0, (summary?.learning_goal || 0) - usedLearned);
      const tbm = Array.isArray(summary?.to_be_mastered) ? summary.to_be_mastered : [];
      const assignedCount = (summary?.has_teacher === true) ? tbm.length : 0;
      const hasLeft = remaining > 0;
      const btnClass = hasLeft ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700';
      const btnText = hasLeft ? `One-click Learn (${remaining} left)` : `Completed today's learning — learned ${usedLearned} words`;
      return (
        <div className="flex justify-between items-center mb-3">
          <div className={`text-sm ${hasLeft ? 'text-purple-700' : 'text-green-700'}`}>
            Today left: <span className="font-semibold">{remaining}</span>
            {assignedCount > 0 && (
              <>
                <span className="mx-2 text-gray-400">|</span>
                Assigned TBM: <span className="font-semibold">{assignedCount}</span>
              </>
            )}
          </div>
          <button
            onClick={hasLeft ? handleOneClickLearnHere : undefined}
            disabled={!hasLeft}
            className={`px-3 py-1.5 text-sm text-white rounded ${btnClass} disabled:opacity-80`}
          >
            {btnText}
          </button>
        </div>
      );
    };
    if (isLoading) {
      return (
        <div>
          <p className="text-center p-8">Loading your study data...</p>
        </div>
      );
    }
    if (error && sessionStatus === 'mode-selection' && !customReviewRequested) {
        return (
          <div>
            <QuickButton />
            <p className="text-center p-8 text-red-500">{error}</p>
          </div>
        );
    }

    switch (sessionStatus) {
        case 'loading':
            return (
              <div>
                <p className="text-center p-8">Preparing practice...</p>
              </div>
            );
        case 'preview':
            return (
              <div className="bg-white p-6 rounded-lg shadow">
                 <div className="flex flex-wrap items-center justify-center gap-3">
                   <h1 className="text-3xl font-extrabold text-gray-900">{t('Preview')}</h1>
                   <TrainingModeBadge mode={trainingMode} />
                 </div>
                 <p className="text-center text-gray-600 mb-6">{t(trainingMode === TRAINING_MODE_COMPREHENSIVE ? 'Preview the words; three listening and speaking steps start next.' : 'Preview meanings to learn; supports quick flashcards')}</p>

                {tierFallbackCount > 0 && (
                  <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-900">
                    {t(tierFallbackCount === 1 ? 'One word is still being prepared for this level, so the closest available exercise level will be used.' : '{count} words are still being prepared for this level, so the closest available exercise level will be used.', { count: tierFallbackCount })}
                  </div>
                )}

                {audioWarmProgress.total > 0 && audioWarmProgress.phase !== 'ready' && (
                  <div className="mb-5 rounded-md border border-indigo-100 bg-indigo-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3 text-sm text-indigo-900">
                      <span>{t(audioWarmProgress.phase === 'words' ? 'Preparing word audio' : 'Preparing practice audio')}</span>
                      <span className="font-semibold tabular-nums">{audioWarmProgress.done}/{audioWarmProgress.total}</span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-indigo-100">
                      <div
                        className="h-full bg-indigo-600 transition-[width] duration-300"
                        style={{ width: `${Math.round((audioWarmProgress.done / Math.max(1, audioWarmProgress.total)) * 100)}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-indigo-700">{t('You can start at any time. Audio continues preparing in the background.')}</p>
                  </div>
                )}

                {/* Toggle */}
                <div className="flex justify-end mb-3">
                  <button
                    className="text-sm px-3 py-1 rounded border bg-white hover:bg-gray-50"
                    onClick={() => {
                      playSoundEffect('previewToggle', { volume: 0.62 });
                      setShowPreviewList(v => !v);
                    }}
                  >
                    {showPreviewList ? 'Show flashcards only' : 'Show word list'}
                  </button>
                </div>

                {/* Word list CN-EN */}
                {showPreviewList && (() => {
                  const perPage = 10;
                  const total = allQuizItems.length;
                  const totalPages = Math.max(1, Math.ceil(total / perPage));
                  const page = Math.min(Math.max(1, previewPage), totalPages);
                  const start = (page - 1) * perPage;
                  const pageItems = allQuizItems.slice(start, start + perPage);
                  const goPrev = () => setPreviewPage(p => Math.max(1, p - 1));
                  const goNext = () => setPreviewPage(p => Math.min(totalPages, p + 1));
                  return (
                  <div className="overflow-x-auto mb-6">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="p-2 text-left">Word</th>
                          <th className="p-2 text-left">Chinese definition</th>
                          <th className="p-2 text-left">English definition</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {pageItems.map((it) => (
                          <tr key={assignmentItemKey(it)}>
                            <td className="p-2 font-semibold text-gray-900 flex items-center gap-2">
                              <span data-i18n-skip>{it.word}</span>
                              <button
                                title="Play pronunciation"
                                className="p-1 rounded hover:bg-gray-100"
                                onClick={() => playPronunciationTap(it.word)}
                              >
                                <SpeakerWaveIcon className="h-5 w-5 text-gray-600" />
                              </button>
                            </td>
                            <td data-i18n-skip className="p-2 text-gray-800">{it.definition.cn}</td>
                            <td data-i18n-skip className="p-2 text-gray-700">{it.definition.en}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-sm text-gray-600">{t('Page {page} of {pages}', { page, pages: totalPages })}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            playSoundEffect('flashcardMove', { volume: 0.58, throttleMs: 70, throttleKey: 'preview-list-prev' });
                            goPrev();
                          }}
                          disabled={page===1}
                          className="px-3 py-1 bg-white border rounded disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => {
                            playSoundEffect('flashcardMove', { volume: 0.58, throttleMs: 70, throttleKey: 'preview-list-next' });
                            goNext();
                          }}
                          disabled={page===totalPages}
                          className="px-3 py-1 bg-white border rounded disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                  );})()}

                {/* Simple flashcards */}
                <PreviewFlashcards items={allQuizItems} />

                <button
                  onClick={() => {
                    playSoundEffect('sectionAdvance', { volume: 0.78 });
                    if (trainingMode === TRAINING_MODE_COMPREHENSIVE) {
                      const comprehensiveProgress = createComprehensiveProgress(allQuizItems);
                      setLearningItems(allQuizItems);
                      setInitialProgress(comprehensiveProgress);
                      setResumeRoundWords(null);
                      setResumeQuestionIndex(0);
                      setSessionStatus('learning');
                    } else {
                      setSessionStatus('pre-test');
                    }
                  }}
                  className="mt-6 w-full bg-indigo-600 text-white px-6 py-3 rounded-md font-semibold text-lg hover:bg-indigo-700"
                >
                  Finish Preview
                </button>
              </div>
            );
        case 'pre-test':
            return (
              <div>
                <PreTestSession
                  items={allQuizItems}
                  onPreTestComplete={handlePreTestComplete}
                  isPureMode={isPureMode}
                  captureMode={captureMode}
                  captureLoading={captureLoading}
                  captureNotice={captureNotice}
                  captureDefaultTitle={captureDefaultTitle}
                  onToggleCapture={handleToggleCapture}
                  onCaptureWord={handleCaptureWord}
                />
              </div>
            );
        case 'learning':
            return <QuizSession 
                        items={learningItems} 
                        initialProgress={initialProgress} 
                        onSessionEnd={handleSessionEnd} 
                         onExit={() => {
                           if (customReviewActive || customReviewRequested) clearCustomReviewState();
                           clearLastSession();
                           clearResume();
                           setSessionStatus('mode-selection');
                        }}
                        onWordRelearned={(item) => {
                          if (practiceMode === 'review') {
                            return updateWordReviewOnBackend(item, reviewResultAfterFourSteps());
                          } else {
                            return masterWordsOnBackend([item], trainingMode).then((saved) => {
                              if (saved) {
                                const masteredKey = assignmentTaskKey(item);
                                setWordsToLearn(prev => prev.filter(candidate => assignmentTaskKey(candidate) !== masteredKey));
                              }
                              return saved;
                            });
                          }
                        }}
                        isPureMode={isPureMode}
                        setIsPureMode={setIsPureMode}
                        practiceMode={practiceMode}
                        tier={tier}
                        resumeRoundWords={resumeRoundWords}
                        resumeQuestionIndex={resumeQuestionIndex}
                        pretestSkipped={pretestSkipped}
                        extraDictationWords={extraDictationWords}
                        fullItems={allQuizItems}
                        onDictationComplete={(assignmentKeys)=>{
                          if (practiceMode === 'review') {
                            (assignmentKeys || []).forEach(key => updateWordReviewOnBackend(key, 'pass'));
                          } else {
                            masterWordsOnBackend(assignmentKeys || []);
                          }
                        }}
                        captureMode={captureMode}
                        captureLoading={captureLoading}
                        captureNotice={captureNotice}
                        captureDefaultTitle={captureDefaultTitle}
                        onToggleCapture={handleToggleCapture}
                        onCaptureWord={handleCaptureWord}
                        sessionId={activeStudySessionId}
                         onPracticeEvent={sendStudySessionEvent}
                         trainingMode={trainingMode}
                         customReviewActive={customReviewActive}
                         customReviewSelection={customReviewSelection}
                         onStartAdaptiveReading={(sessionId, options = {}) => {
                          if (Array.isArray(options.followupModes) && options.followupModes.length && Array.isArray(options.followupWords)) {
                            try {
                              sessionStorage.setItem(
                                'lexilab.post-learning-followup',
                                JSON.stringify({
                                  words: options.followupWords,
                                  modes: options.followupModes,
                                  source_session_id: sessionId,
                                  created_at: new Date().toISOString(),
                                }),
                              );
                            } catch (_) {}
                           }
                           sendStudySessionEvent({ event: 'completed' }, sessionId);
                           if (customReviewActive) clearCustomReviewSelection();
                           clearLastSession();
                           clearResume();
                          if (options.startReading) {
                            const params = new URLSearchParams({
                              scope: 'last_session',
                              source_session_id: sessionId,
                            });
                            if (options.followupModes?.length) params.set('after', 'followup');
                            navigate(`/student/self-check/adaptive-reading?${params.toString()}`);
                          } else {
                            navigate('/student/self-check?post_learning_followup=1');
                          }
                        }}
                    />;
        case 'finished':
            return (
                <div className="w-full min-w-0 bg-white p-5 sm:p-8 rounded-lg shadow-md text-center">
                    <SparklesIcon className="mx-auto h-16 w-16 text-green-500" />
                    <h1 className="mt-4 text-4xl font-extrabold text-gray-900">{t('Practice complete!')}</h1>
                    <div className="mt-3 flex justify-center"><TrainingModeBadge mode={trainingMode} /></div>
                    {practiceMode === 'review' && customReviewActive ? (
                      <div className="mt-5 rounded-md border bg-indigo-50 p-4 text-left">
                        <p className="font-semibold text-indigo-900">
                          {t('Custom review completed: {count}/{total} selected meanings.', {
                            count: customReviewTotal,
                            total: customReviewTotal,
                          })}
                        </p>
                        <p className="mt-1 text-sm text-indigo-800">
                          {t('{count} due meanings counted toward today’s review; {extra} were extra practice.', {
                            count: customReviewDueCount,
                            extra: Math.max(0, customReviewTotal - customReviewDueCount),
                          })}
                        </p>
                        <p className="mt-2 text-xs text-indigo-700">
                          {t('Extra practice meanings keep their original review schedule.')}
                        </p>
                      </div>
                    ) : practiceMode === 'review' && reviewPlan ? (
                      <div className="mt-5 rounded-md border bg-purple-50 p-4 text-left">
                        <p className="font-semibold text-purple-900">
                          {t('This round completed {count} meanings.', {
                            count: Math.max(0, Number(reviewPlan.completed_today || 0) - reviewCompletedAtStart),
                          })}
                        </p>
                        <p className="mt-1 text-sm text-purple-800">
                          {reviewPlan.completion_reason === 'no_review_due'
                            ? t('No review due today')
                            : reviewPlan.review_complete
                              ? t('Today’s goal is complete. {count} meanings remain in the queue.', { count: reviewPlan.remaining_due })
                              : t('{remaining} more to reach today’s goal. {count} meanings remain in the queue.', {
                                remaining: reviewPlan.remaining_required,
                                count: reviewPlan.remaining_due,
                              })}
                        </p>
                        <p className="mt-2 text-xs text-purple-700">
                          {t('Today: {done}/{goal}', { done: reviewPlan.completed_today, goal: reviewPlan.required_today })}
                        </p>
                      </div>
                    ) : (
                      <p className="mt-2 text-lg text-gray-600">{t('Great job! Keep it up!')}</p>
                    )}
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {practiceMode === 'review' && !customReviewActive && Number(reviewPlan?.remaining_due || 0) > 0 && (
                        <button
                          onClick={() => {
                            setDesiredReviewCount(Math.max(1, Number(reviewPlan?.default_batch_count || 1)));
                            setSessionStatus('tier-selection');
                          }}
                          className="w-full bg-purple-600 text-white px-6 py-4 rounded-md font-bold hover:bg-purple-700"
                        >
                          {t('Review more')}
                        </button>
                      )}
                      <button onClick={() => navigate('/student/dashboard')} className="w-full bg-blue-600 text-white px-6 py-4 rounded-md font-bold hover:bg-blue-700">
                          {t('Temporarily finish')}
                      </button>
                      <button onClick={() => navigate('/student/word-overview')} className="w-full sm:col-span-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-md font-semibold hover:bg-gray-50">
                          {t('Open word overview')}
                      </button>
                    </div>
                </div>
            );
        case 'tier-selection':
            const allTiers = TIER_ORDER_EASY_TO_HARD;
            
            return (
                <div className="w-full min-w-0 bg-white p-5 sm:p-8 rounded-lg shadow-md text-center">
                     <BeakerIcon className="mx-auto h-12 w-12 text-purple-600" />
                     <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-gray-900">Word Practice</h1>
                     <p className="mt-2 text-lg text-gray-600">{t('Choose quantity, training mode, and difficulty before starting.')}</p>
                     <TrainingModeSelector
                       value={trainingMode}
                       onChange={handleTrainingModeChange}
                       counts={practiceMode === 'review' ? reviewModeCounts : learningModeCounts}
                       showCounts
                       legend="Training mode"
                       name="word-practice-tier-mode"
                       className="mx-auto mt-6 max-w-2xl text-left"
                     />
                    
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                        {allTiers.map((tierOption, tierIndex) => {
                            const isAvailable = availableTiers.includes(tierOption);
                            const isSelected = tier === tierOption;
                            
                            return (
                                <button 
                                    key={tierOption}
                                    onClick={() => isAvailable && setTier(tierOption)}
                                    disabled={!isAvailable}
                                    className={`min-h-28 sm:min-h-32 p-4 rounded-lg text-left transition-all duration-200 ${
                                        isSelected 
                                            ? 'bg-purple-600 text-white shadow-lg scale-105' 
                                            : isAvailable 
                                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    <span className="block text-xs font-semibold uppercase tracking-wider opacity-70">{t('Level {level}', { level: tierIndex + 1 })}</span>
                                    <span className="mt-1 block text-lg font-bold">{TIER_INFO[tierOption].studentLabel}</span>
                                    <span className="mt-2 block text-sm font-normal leading-snug opacity-80">{TIER_INFO[tierOption].studentDescription}</span>
                                    {!isAvailable && (
                                        <span className="block text-sm mt-2">(Unavailable)</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {practiceMode === 'learn' && focusWords.length === 0 && focusAssignments.length === 0 && (
                      <div className="mt-8 text-left">
                        <div className="p-4 rounded-lg border bg-indigo-50 border-indigo-200">
                          <div className="font-bold text-indigo-900 mb-2">How many to study this time?</div>
                          <div className="text-indigo-800 text-sm mb-3">
                             {t('Available meanings: {count}', { count: modeLearningItems.length })}
                             {teacherAssigned.length > 0 && (
                               <>{t('Assigned meanings {assigned}, self-study {selfStudy}', { assigned: teacherAssigned.filter((item) => getTrainingMode(item) === trainingMode).length, selfStudy: Math.max(0, modeLearningItems.length - teacherAssigned.filter((item) => getTrainingMode(item) === trainingMode).length) })}</>
                             )}
                          </div>
                          <div className="flex items-center gap-3">
                            <input
                              type="range"
                              min="1"
                               max={Math.max(1, modeLearningItems.length)}
                               value={Math.min(desiredLearnCount || 1, Math.max(1, modeLearningItems.length))}
                              onChange={(e)=>setDesiredLearnCount(parseInt(e.target.value, 10))}
                              className="flex-1"
                            />
                            <input
                              type="number"
                              min="1"
                               max={Math.max(1, modeLearningItems.length)}
                               value={desiredLearnCount || 1}
                               onChange={(e)=>setDesiredLearnCount(Math.min(Math.max(1, parseInt(e.target.value || '1', 10)), Math.max(1, modeLearningItems.length)))}
                              className="w-24 p-2 border rounded"
                            />
                          </div>
                          <div className="text-xs text-indigo-700 mt-2">
                            Default: prioritize assigned words, then supplement with self-study.
                          </div>
                        </div>
                      </div>
                    )}

                    {practiceMode === 'review' && (
                      <div className="mt-8 text-left">
                        <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
                          <div className="font-bold text-purple-900 mb-2">{t('How many meanings to review?')}</div>
                          <div className="text-purple-800 text-sm mb-3">
                            {t('{done}/{goal} meanings completed', {
                              done: reviewPlan?.completed_today || 0,
                              goal: reviewPlan?.required_today || 0,
                            })} · {t('{count} meanings in review queue', { count: reviewModeCounts[trainingMode] || 0 })}
                          </div>
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              min="1"
                              max={Math.max(1, Number(reviewModeCounts[trainingMode] || 1))}
                              value={desiredReviewCount}
                              onChange={(event) => setDesiredReviewCount(Number.parseInt(event.target.value, 10) || 1)}
                              className="w-28 p-2 border rounded bg-white"
                            />
                            <span className="text-sm text-purple-700">{t('This changes only this batch, not your daily goal.')}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {practiceMode === 'review' && (
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setShowCustomReview(true);
                        }}
                        className="mt-4 rounded-md border border-purple-300 bg-white px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
                      >
                        {t('Choose specific meanings instead')}
                      </button>
                    )}
                    
                    {error && <p className="mt-4 text-red-500 font-semibold">{error}</p>}
                    
                    <div className="mt-8">
                        <button 
                            onClick={() => {
                              if (practiceMode === 'learn') startLearnSession(tier, trainingMode);
                              else if (practiceMode === 'review') startReviewSession(tier, trainingMode);
                              else {
                                setError('Unrecognized practice mode. Please go back and select again.');
                              }
                            }} 
                            disabled={!tier || (practiceMode !== 'learn' && practiceMode !== 'review')} 
                            className="w-full bg-green-600 text-white px-8 py-4 rounded-md font-bold text-xl hover:bg-green-700 transition-transform transform hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            Start
                        </button>
                    </div>
                </div>
            );
        case 'mode-selection':
        default:
            return (
                 <div className="bg-white p-8 rounded-lg shadow-md text-center">
                     <h1 className="text-4xl font-extrabold text-gray-900">{t('What to learn today?')}</h1>
                     <p className="mt-2 text-lg text-gray-600">{t('Choose a mode to start your learning journey.')}</p>
                     {customReviewMissing && (
                       <p className="mx-auto mt-4 max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-900">
                         {t('Your custom review selection could not be restored. Choose the meanings again to continue.')}
                       </p>
                     )}
                     <TrainingModeSelector
                       value={trainingMode}
                       onChange={handleTrainingModeChange}
                       counts={learningModeCounts}
                       showCounts
                       legend="Learning mode"
                       name="word-practice-learning-mode"
                       className="mx-auto mt-6 max-w-2xl text-left"
                     />
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Learn Words */}
                        <div className="flex flex-col">
                             {modeLearningItems.length > 0 ? (
                                <button onClick={() => handleModeSelect('learn')} className="p-8 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-all transform hover:scale-105 shadow-lg">
                                    <BookOpenIcon className="mx-auto h-12 w-12" />
                                    <h2 className="mt-4 text-2xl font-bold">Learn new meanings</h2>
                                     <p className="mt-2 text-4xl font-black">{modeLearningItems.length}</p>
                                     <p>{t('meanings ready in this mode')}</p>
                                    {teacherAssigned.length > 0 && (
                                      <p className="mt-2 text-sm opacity-90">{t('Assigned meanings {assigned}, self-study {selfStudy}', { assigned: teacherAssigned.length, selfStudy: Math.max(0, wordsToLearn.length - teacherAssigned.length) })}</p>
                                    )}
                                </button>
                            ) : (
                                <div className="p-8 rounded-lg bg-green-100 text-green-800 text-center border-2 border-green-200">
                                    <SparklesIcon className="mx-auto h-12 w-12" />
                                    <h2 className="mt-4 text-2xl font-bold">No new meanings!</h2>
                                    <p className="mt-2 text-lg">{t('You’ve completed all assigned tasks. Great discipline!')}</p>
                                    <div className="mt-6 text-gray-700">
                                      Want to keep learning? Click
                                      <button
                                        type="button"
                                        onClick={() => {
                                          playSoundEffect('learnNowOpen', { volume: 0.78 });
                                          navigate('/student/dashboard?openQuick=1');
                                        }}
                                        className="mx-2 inline-flex items-center px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                      >
                                        Learn Now
                                      </button>
                                      , to supplement new words from other wordbooks.
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Review Words */}
                        <div className="flex flex-col">
                             {Number(reviewModeCounts[trainingMode] || 0) > 0 ? (
                                <div className="flex flex-col gap-3">
                                  <button onClick={() => handleModeSelect('review')} className="p-8 rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-all transform hover:scale-105 shadow-lg">
                                      <SparklesIcon className="mx-auto h-12 w-12" />
                                      <h2 className="mt-4 text-2xl font-bold">Review old words</h2>
                                      <p className="mt-2 text-4xl font-black">{reviewModeCounts[trainingMode] || 0}</p>
                                      <p>{t('available in this mode')}</p>
                                  </button>
                                  <button type="button" onClick={openCustomReviewSelector} className="rounded-md border border-purple-300 bg-white px-4 py-2.5 font-semibold text-purple-700 hover:bg-purple-50">
                                    {t('Custom review')}
                                  </button>
                                </div>
                             ) : (
                                <div className="p-8 rounded-lg bg-green-100 text-green-800 text-center border-2 border-green-200">
                                    <SparklesIcon className="mx-auto h-12 w-12" />
                                    <h2 className="mt-4 text-2xl font-bold">No reviews today!</h2>
                                    <p className="mt-2 text-lg">{t('Everything is firmly remembered — proud of you!')}</p>
                                    <button type="button" onClick={openCustomReviewSelector} className="mt-6 rounded-md border border-purple-300 bg-white px-4 py-2.5 font-semibold text-purple-700 hover:bg-purple-50">
                                      {t('Custom review')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
    }
  }

  return (
    <main className="min-h-screen min-w-0 bg-gray-100 flex flex-col items-center justify-center p-0 sm:p-4">
      <div className="w-full min-w-0 max-w-4xl mx-auto">
        {sessionStatus === 'pre-test-results' && (
            <PreTestResultsModal 
              knownWords={preTestResults.correct}
              items={allQuizItems}
              onConfirm={handleConfirmPreTestChoices}
              isPureMode={isPureMode}
            />
        )}
        <WordCaptureDialog
          selection={captureSelection}
          wordbooks={captureSettings?.wordbooks || []}
          lookup={captureLookup}
          lookupLoading={captureLookupLoading}
          lookupError={captureLookupError}
          saving={captureSaving}
          error={captureError}
          onClose={() => {
            if (!captureSaving) {
              captureLookupRequestRef.current += 1;
              setCaptureSelection(null);
              setCaptureLookup(null);
              setCaptureLookupError('');
              setCaptureLookupLoading(false);
              setCaptureError('');
            }
          }}
          onWordbookChange={(wordbookId) => {
            setCaptureSelection((previous) => (
              previous ? { ...previous, wordbookId } : previous
            ));
            setCaptureError('');
          }}
          onPlayPronunciation={playDictionaryPronunciation}
          onConfirm={handleConfirmCapture}
        />
        {renderContent()}
        {error && customReviewRequested && sessionStatus === 'mode-selection' && (
          <div role="alert" className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-4 text-rose-800">
            <p>{error}</p>
            {customReviewSelection.length > 0 && (
              <button type="button" onClick={() => startReviewSession()} className="mt-3 rounded-md bg-white px-4 py-2 font-semibold">
                {t('Retry')}
              </button>
            )}
          </div>
        )}
        <CustomReviewSelector
          open={showCustomReview}
          onClose={() => setShowCustomReview(false)}
          onStart={handleCustomReviewStart}
          availableTiers={availableTiers}
          initialTier={tier || studentDefaultTier || studentTier || DEFAULT_TIER}
          initialTrainingMode={trainingMode}
        />
      </div>
    </main>
  );
};

export default WordPracticePage;

// --- Simple Flashcards for Preview ---
const PreviewFlashcards = ({ items }) => {
  const { t } = useI18n();
  const [index, setIndex] = React.useState(0);
  const [showBack, setShowBack] = React.useState(false);
  const total = items.length || 0;
  const current = items[index] || null;

  if (total === 0) return null;

  const prev = () => {
    playSoundEffect('flashcardMove', { volume: 0.6, throttleMs: 60, throttleKey: 'flashcard-prev' });
    setShowBack(false);
    setIndex(i => (i > 0 ? i - 1 : total - 1));
  };
  const next = () => {
    playSoundEffect('flashcardMove', { volume: 0.6, throttleMs: 60, throttleKey: 'flashcard-next' });
    setShowBack(false);
    setIndex(i => (i < total - 1 ? i + 1 : 0));
  };

  return (
    <div className="border rounded-md p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">{t('Flashcard {current}/{total}', { current: index + 1, total })}</div>
        <div className="flex gap-2">
          <button onClick={prev} className="px-3 py-1 bg-white border rounded hover:bg-gray-100">{t('Previous')}</button>
          <button onClick={next} className="px-3 py-1 bg-white border rounded hover:bg-gray-100">{t('Next')}</button>
        </div>
      </div>
      <div className="p-6 bg-white rounded border text-center">
        {!showBack ? (
          <div>
            <div className="text-gray-500 text-sm mb-2">Word</div>
            <div className="text-3xl font-extrabold text-gray-900 flex items-center justify-center gap-3">
              <span data-i18n-skip>{current.word}</span>
              <button
                title="Play pronunciation"
                onClick={() => playPronunciationTap(current.word)}
                className="p-2 rounded hover:bg-gray-100"
              >
                <SpeakerWaveIcon className="h-6 w-6 text-gray-700" />
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-gray-500 text-sm mb-2">Definition</div>
            <div data-i18n-skip className="text-lg font-semibold text-gray-800">{current.definition.cn}</div>
            <div data-i18n-skip className="text-sm text-gray-600 mt-1">{current.definition.en}</div>
          </div>
        )}
      </div>
      <div className="mt-3 text-center">
        <button
          onClick={() => {
            playSoundEffect('flashcardFlip', { volume: 0.68, throttleMs: 70, throttleKey: `flashcard-flip-${index}` });
            setShowBack(s => !s);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {showBack ? 'Show word' : 'Show definition'}
        </button>
      </div>
    </div>
  );
};
